import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  CrmActionItem,
  CrmActionKind,
  CrmActionLevel,
  CrmAdminProfile,
  CrmActivityAction,
  CrmActivityEntityType,
  CrmBillingSyncStatus,
  CrmBreakdownItem,
  CrmClientActivity,
  CrmClientBilling,
  CrmClientChangeLogEntry,
  CrmClientContactOption,
  CrmClientDetailsData,
  CrmClientDates,
  CrmClientFieldChange,
  CrmClientNetwork,
  CrmClientNote,
  CrmClientReferences,
  CrmClientReferral,
  CrmClientReferralOption,
  CrmClientSummary,
  CrmClientUser,
  CrmCommunicationThread,
  CrmCompanyProfile,
  CrmCompanyStage,
  CrmCompanyType,
  CrmContact,
  CrmContactPerson,
  CrmDetailsSection,
  CrmDropdownOptions,
  CrmDuplicateCandidate,
  CrmEntityType,
  CrmLcContact,
  CrmLcMetrics,
  CrmLcNetwork,
  CrmLcNetworkInfo,
  CrmLcPrimaryContact,
  CrmLcSubscription,
  CrmLcTokenBreakdown,
  CrmManagedVendorLc,
  CrmMarketingProfile,
  CrmPrimaryNetworkOption,
  CrmQuickBooksCustomerPlaceholder,
  CrmQuickBooksInvoicePlaceholder,
  CrmSupportTicketPlaceholder,
  CrmVendorLcDetail
} from '../crm.types';
import {
  CRM_DATE_FORMAT_OPTIONS,
  CRM_DATE_FORMAT_STRINGS,
  CrmDateFormatKey
} from '../crm-components/crm-date-format-options';
import { CrmPrintMenuOption } from '../crm-components/crm-print-menu.component';
import { formatPrintDate, printCrmReport, renderFieldCards, renderFieldGrid, renderMetricGrid, renderPrintSection, renderTable } from '../crm-components/crm-print.utils';
import { CrmCurrentUser, CrmService } from '../crm.service';
import { TabsNavigationItems } from '../../common/elements/elements.types';

interface CrmTableHeader {
  headerName: string;
  field: string;
  sortable?: boolean;
}

type CrmDetailsSupplementalRequest = 'lcData' | 'metrics' | 'users' | 'vendorLcs';
type CrmDetailDropdownTarget = 'source' | 'industry';
type CrmDetailTagDropdownTarget = 'companyProfileTags' | 'overviewTags' | 'primaryContactTags' | 'additionalContactTags';
type CrmDetailsViewMode = 'detailed' | 'simple';

interface CrmClientNetworkRow {
  network: CrmClientNetwork;
  primaryContact: CrmLcPrimaryContact | null;
  hasAttachedLc: boolean;
  clientName: string;
  lcCount: number;
  clientStage: CrmCompanyStage;
  clientType: CrmCompanyType;
  lcName: string;
  contactName: string;
  contactEmail: string;
  crmStatus: string;
  primaryLabel: string;
  tokenUsageLabel: string;
  createdDate: string;
}

interface CrmClientUserRow {
  user: CrmClientUser;
  userName: string;
  email: string;
  roleLabel: string;
  highlightedRoles: string[];
  groupsLabel: string;
  statusLabel: string;
  lastActivityDate: string;
}

interface CrmClientChangeLogRow {
  entry: CrmClientChangeLogEntry;
  changedDate: string;
  actor: string;
  scopeLabel: string;
  target: string;
  actionLabel: string;
  changesSummary: string;
}

interface CrmContactTableRow extends CrmContactPerson {
  tableRowId: string;
  contactRoleLabel: string;
  canDelete: boolean;
}

interface CrmManagedVendorLcRow {
  lc: CrmManagedVendorLc;
  networkName: string;
  networkIdLabel: string;
  statusLabel: string;
  createdDate: string;
}

interface CrmNetworkInfoEntry {
  label: string;
  value: string | number | null;
  isDate?: boolean;
  stacked?: boolean;
}

interface CrmNetworkInfoDraft {
  subscriptionStart: string;
  subscriptionEnd: string;
  paidUntil: string;
  nextInvoice: string;
  nextPaymentDue: string;
  sendOn: string;
  repeatDays: number;
  emailReminder: boolean;
  advancedPayment: boolean;
  reference: string;
  notes: string;
}

interface CrmClientEditDraft {
  clientName: string;
  legalName: string;
  stage: CrmCompanyStage;
  type: CrmCompanyType | '';
  source: string;
  converted: string;
  industry: string;
  website: string;
  phone: string;
  emailGeneral: string;
  addressText: string;
  tagsCsv: string;
  notesSummary: string;
  primaryContactId: string;
  primaryContactVendorId: string;
  primaryContactFirstName: string;
  primaryContactLastName: string;
  primaryContactTitle: string;
  primaryContactEmail: string;
  primaryContactAltEmail: string;
  primaryContactPhone: string;
  primaryContactAltPhone: string;
  primaryContactPhoneNorm: string;
  primaryContactPreferredContact: string;
  primaryContactCompanyId: string;
  primaryContactUserId: string;
  primaryContactRolesCsv: string;
  primaryContactIsPrimary: boolean;
  primaryContactTagsCsv: string;
  primaryContactNotes: string;
  primaryContactCreated: string;
  primaryContactModified: string;
  primaryContactDeleted: string;
  billingContactName: string;
  billingEmail: string;
  billingPhone: string;
  primaryRegistrarEmail: string;
  alternateAccountContactName: string;
  alternateAccountContactEmail: string;
  alternateAccountContactPhone: string;
  billingInstructions: string;
  lcNetworkId: string;
  lcName: string;
  lcLoginPage: string;
  lcSubscriptionStart: string;
  lcSubscriptionEnd: string;
  lcAdvancePay: boolean;
  lcInvoiceRef: string;
  lcNote: string;
  referralSourceCompanyId: string;
  referralSourceCompanyName: string;
  referralSourceContactId: string;
  referralSourceContactName: string;
  referralProgram: string;
  referred: string;
  referralNotes: string;
}

interface CrmAdditionalContactDraft {
  firstName: string;
  lastName: string;
  department: string;
  title: string;
  position: string;
  email: string;
  phone: string;
  officePhone: string;
  officeExtension: string;
  cellPhone: string;
  preferredContact: string;
  rolesCsv: string;
  tagsCsv: string;
  note: string;
}

interface CrmManualNoteDraft {
  entityType: CrmEntityType;
  pinned: boolean;
  body: string;
}

interface CrmEditNoteDraft {
  id: number;
  entityType: CrmEntityType;
  pinned: boolean;
  body: string;
}

interface CrmManualActivityDraft {
  entityType: CrmActivityEntityType;
  created: string;
  action: CrmActivityAction | '';
  details: string;
}

interface CrmManualActionDraft {
  level: CrmActionLevel;
  action: CrmActionKind | '';
  details: string;
  actionDate: string;
  dueDate: string;
}

interface CrmDeletedContactActivityDetails {
  title: string;
  userId: string;
  preferredContact: string;
}

interface CrmActivityFieldChange extends CrmClientFieldChange {
  hasPreviousValue: boolean;
}

interface CrmClientLinkFormUpdates {
  name: string;
  stage: CrmCompanyStage;
  type: CrmCompanyType;
}

const DEFAULT_EXPANDED_SECTIONS: Record<CrmDetailsSection, boolean> = {
  companyProfile: true,
  subscriptionInfo: true,
  overview: false,
  primaryContact: false,
  contactPersons: false,
  billing: false,
  managedVendorLcs: false,
  referral: false,
  actions: true,
  communications: false,
  support: false,
  quickbooks: false,
  marketing: false,
  admin: false,
  notesActivity: false,
  networks: false,
  users: false,
  changelog: false
};

type CrmEditableDetailsSection = 'companyProfile' | 'lcInfo' | Extract<CrmDetailsSection, 'overview' | 'primaryContact' | 'billing' | 'referral'>;

const DEFAULT_EDITING_SECTIONS: Record<CrmEditableDetailsSection, boolean> = {
  companyProfile: false,
  lcInfo: false,
  overview: false,
  primaryContact: false,
  billing: false,
  referral: false
};

const CRM_COMPANY_STAGE_OPTIONS: CrmCompanyStage[] = ['prospect', 'proposal', 'trial', 'client', 'at_risk', 'paused', 'lost'];
const CRM_COMPANY_TYPE_OPTIONS: CrmCompanyType[] = ['lead', 'customer', 'partner', 'internal'];
const CRM_CLIENT_ACTION_LEVEL_OPTIONS: CrmActionLevel[] = ['Low', 'Medium', 'High', 'Critical'];
const CRM_CLIENT_ACTION_KIND_OPTIONS: CrmActionKind[] = ['follow_up_phone', 'follow_up_email', 'send_invoice', 'set_up_lc', 'suspend_lc', 'other'];
const CRM_DASHBOARD_VIEW_MODE_STORAGE_KEY = 'crm.dashboardViewMode';
const EMPTY_LC_TOKEN_BREAKDOWN: CrmLcTokenBreakdown = {
  total: 0,
  used: 0,
  unused: 0,
  byRole: []
};
const EMPTY_CRM_DROPDOWNS: CrmDropdownOptions = {
  source: [],
  industry: [],
  client_tags: [],
  user_tags: []
};

@Component({
  selector: 'app-crm-details',
  templateUrl: './crm-details.component.html',
  styleUrls: ['../crm.scss', './crm-details.component.scss']
})
export class CrmDetailsComponent implements OnInit {
  clientId = '';
  companyId = '';
  networkId = '';
  clientName = '';
  vendorLc: CrmVendorLcDetail | null = null;
  tokenBreakdown: CrmLcTokenBreakdown = EMPTY_LC_TOKEN_BREAKDOWN;
  crmDropdowns: CrmDropdownOptions = { ...EMPTY_CRM_DROPDOWNS };
  activeDetailDropdown: CrmDetailDropdownTarget | null = null;
  activeDetailTagDropdown: CrmDetailTagDropdownTarget | null = null;
  deletingDetailDropdownItems = new Set<string>();
  summary: CrmClientSummary | null = null;
  primaryContact: CrmContact | null = null;
  companyProfile: CrmCompanyProfile | null = null;
  contactPersons: CrmContactPerson[] = [];
  contactTableRows: CrmContactTableRow[] = [];
  dates: CrmClientDates | null = null;
  references: CrmClientReferences | null = null;
  actions: CrmActionItem[] = [];
  communications: CrmCommunicationThread[] = [];
  supportTickets: CrmSupportTicketPlaceholder[] = [];
  quickbooksCustomer: CrmQuickBooksCustomerPlaceholder | null = null;
  quickbooksInvoices: CrmQuickBooksInvoicePlaceholder[] = [];
  marketing: CrmMarketingProfile | null = null;
  admin: CrmAdminProfile | null = null;
  duplicateSearchTerm = '';
  duplicateSearchResults: CrmDuplicateCandidate[] = [];
  selectedLcContactNetwork: CrmClientNetwork | null = null;
  selectedLcContactEntries: Array<{ label: string; value: string }> = [];
  showAddContactModal = false;
  addContactError = '';
  contactDeleteError = '';
  isSavingAdditionalContact = false;
  additionalContactDraft: CrmAdditionalContactDraft = this.getDefaultAdditionalContactDraft();
  editingAdditionalContactId: number | null = null;
  showAddNoteModal = false;
  showEditNoteModal = false;
  showAddActivityModal = false;
  showAddActionModal = false;
  showCompleteActionConfirmModal = false;
  addNoteError = '';
  editNoteError = '';
  addActivityError = '';
  addActionError = '';
  completeActionError = '';
  isSavingManualNote = false;
  isSavingEditNote = false;
  isSavingManualActivity = false;
  isSavingClientAction = false;
  manualNoteDraft: CrmManualNoteDraft = this.getDefaultManualNoteDraft();
  editNoteDraft: CrmEditNoteDraft | null = null;
  manualActivityDraft: CrmManualActivityDraft = this.getDefaultManualActivityDraft();
  manualActionDraft: CrmManualActionDraft = this.getDefaultManualActionDraft();
  actionPendingCompletion: CrmActionItem | null = null;
  editingActionId: number | null = null;
  readonly manualLogEntityTypeOptions: CrmEntityType[] = ['company', 'lc', 'contact'];
  readonly manualActivityActionOptions: CrmActivityAction[] = ['contact', 'support', 'referral'];
  readonly clientActionLevelOptions = CRM_CLIENT_ACTION_LEVEL_OPTIONS;
  readonly clientActionKindOptions = CRM_CLIENT_ACTION_KIND_OPTIONS;
  billing: CrmClientBilling | null = null;
  referral: CrmClientReferral | null = null;
  referralCompanyOptions: CrmClientReferralOption[] = [];
  referralContactOptions: CrmClientContactOption[] = [];
  notes: CrmClientNote[] = [];
  activities: CrmClientActivity[] = [];
  networks: CrmClientNetwork[] = [];
  network: CrmLcNetwork | null = null;
  networkInfo: CrmLcNetworkInfo | null = null;
  isEditingNetworkInfo = false;
  networkInfoDraft: CrmNetworkInfoDraft = this.getDefaultNetworkInfoDraft();
  isSavingNetworkInfo = false;
  networkInfoError = '';
  metrics: CrmLcMetrics | null = null;
  primaryLcContact: CrmLcPrimaryContact | null = null;
  lcContacts: CrmLcContact[] = [];
  subscription: CrmLcSubscription | null = null;
  isVendorClient = false;
  managedVendorLcs: CrmManagedVendorLc[] = [];
  managedVendorLcRows: CrmManagedVendorLcRow[] = [];
  addClientName = '';
  addClientStage: CrmCompanyStage = 'prospect';
  addClientType: CrmCompanyType = 'lead';
  addLcSearchTerm = '';
  showAddLcsModal = false;
  showRemoveLcLinkConfirmModal = false;
  crmUser: CrmCurrentUser | null = null;
  crmLoginUsername = '';
  crmLoginPassword = '';
  crmLoginError = '';
  isCrmLoggingIn = false;
  isLoadingDetails = true;
  isLoadingDetailsEnrichment = false;
  isLoadingDetailsLcData = false;
  isLoadingDetailsMetrics = false;
  isLoadingDetailsUsers = false;
  isLoadingDetailsManagedVendorLcs = false;
  detailsError = '';
  dropdownDeleteError = '';
  addLcsError = '';
  removeLcLinkError = '';
  isRemovingLcLink = false;
  headerFieldError = '';
  savingHeaderFields = new Set<string>();
  addLcOptionsError = '';
  isLoadingAddLcOptions = false;
  addLcOptions: CrmPrimaryNetworkOption[] = [];
  selectedAddLcNetworkIds: number[] = [];
  addLcOptionsPage = 1;
  editingClientNetworkRow: CrmClientNetworkRow | null = null;
  users: CrmClientUser[] = [];
  changelog: CrmClientChangeLogEntry[] = [];
  allNetworkTableRows: CrmClientNetworkRow[] = [];
  networkTableRows: CrmClientNetworkRow[] = [];
  userTableRows: CrmClientUserRow[] = [];
  actionTableRows: CrmActionItem[] = [];
  changelogTableRows: CrmClientChangeLogRow[] = [];
  showCompletedActions = true;
  editDraft: CrmClientEditDraft | null = null;
  hasPendingChanges = false;
  isSavingDraft = false;
  saveDraftError = '';
  expandedSections: Record<CrmDetailsSection, boolean> = { ...DEFAULT_EXPANDED_SECTIONS };
  editingSections: Record<CrmEditableDetailsSection, boolean> = { ...DEFAULT_EDITING_SECTIONS };
  private detailsEnrichmentRequestId = 0;
  dateFormat: CrmDateFormatKey = 'Default';
  dateFormatStrings = CRM_DATE_FORMAT_STRINGS;
  dateNow = new Date();
  detailsViewMode: CrmDetailsViewMode = this.getStoredDetailsViewMode();
  detailsViewTabs: TabsNavigationItems[] = this.buildDetailsViewTabs(this.detailsViewMode);
  readonly dateFormatOptions = CRM_DATE_FORMAT_OPTIONS;
  private readonly detailedDetailsHeaderMetricSkeletonItems = [1, 2, 3, 4];
  private readonly simpleDetailsHeaderMetricSkeletonItems: number[] = [];
  private readonly detailedDetailsInsightSkeletonItems = [1, 2];
  private readonly simpleDetailsInsightSkeletonItems: number[] = [];
  readonly detailsInsightRowSkeletonItems = [1, 2, 3, 4, 5];
  private readonly detailedDetailsOpsSkeletonItems = [1, 2];
  private readonly simpleDetailsOpsSkeletonItems = [1];
  readonly detailsFieldSkeletonItems = [1, 2, 3, 4, 5, 6];
  private readonly detailedDetailsSectionSkeletonItems = [1, 2, 3, 4, 5, 6];
  private readonly simpleDetailsSectionSkeletonItems = [1, 2, 3, 4];
  readonly detailsTableSkeletonRows = [1, 2, 3, 4, 5];
  readonly detailsTableSkeletonCells = [1, 2, 3, 4, 5, 6];
  private initialEditDraftSnapshot = '';
  private keepNotesActivityExpandedOnNextLoad = false;
  private keepActionsExpandedOnNextLoad = false;
  networkSearchTerm = '';
  selectedNetworkStage: 'all' | CrmCompanyStage = 'all';
  selectedNetworkType: 'all' | CrmCompanyType = 'all';
  readonly networkPageSizeOptions = [10, 25, 50, 100];
  networkItemsPerPage = 10;
  currentNetworkPage = 1;
  readonly addLcOptionsPerPage = 8;
  readonly userItemsPerPage = 8;
  readonly managedVendorLcItemsPerPage = 10;
  readonly actionItemsPerPage = 10;
  readonly changelogItemsPerPage = 8;
  readonly activityPageSizeOptions = [5, 10, 25, 50];
  activityItemsPerPage = 5;
  currentActivityPage = 1;
  readonly stageOptions = CRM_COMPANY_STAGE_OPTIONS;
  readonly typeOptions = CRM_COMPANY_TYPE_OPTIONS;
  readonly userTableHeaders: CrmTableHeader[] = [
    { headerName: 'User', field: 'userName', sortable: false },
    { headerName: 'Email', field: 'email', sortable: false },
    { headerName: 'Role', field: 'roleLabel', sortable: false },
    { headerName: 'Groups', field: 'groupsLabel', sortable: false },
    { headerName: 'Status', field: 'statusLabel', sortable: false },
    { headerName: 'Last Activity', field: 'lastActivityDate', sortable: false }
  ];
  readonly contactTableHeaders: CrmTableHeader[] = [
    { headerName: 'Contact', field: 'displayName', sortable: false },
    { headerName: 'Department', field: 'department', sortable: false },
    { headerName: 'Position', field: 'position', sortable: false },
    { headerName: 'Email', field: 'email', sortable: false },
    { headerName: 'Phone', field: 'phone', sortable: false },
    { headerName: 'Notes', field: 'contact_note', sortable: false },
    { headerName: 'Actions', field: 'actions', sortable: false }
  ];
  readonly actionTableHeaders: CrmTableHeader[] = [
    { headerName: 'Level', field: 'level', sortable: false },
    { headerName: 'Action', field: 'summary', sortable: false },
    { headerName: 'Details', field: 'details', sortable: false },
    { headerName: 'Created By', field: 'createdBy', sortable: false },
    { headerName: 'Due Date', field: 'actionDate', sortable: false },
    { headerName: 'Created', field: 'created', sortable: false },
    { headerName: 'Status', field: 'status', sortable: false },
    { headerName: 'Actions', field: 'actions', sortable: false }
  ];
  readonly managedVendorLcTableHeaders: CrmTableHeader[] = [
    { headerName: 'LC', field: 'networkName', sortable: false },
    { headerName: 'Network ID', field: 'networkIdLabel', sortable: false },
    { headerName: 'Status', field: 'statusLabel', sortable: false },
    { headerName: 'Created', field: 'createdDate', sortable: false }
  ];
  readonly changelogTableHeaders: CrmTableHeader[] = [
    { headerName: 'Timestamp', field: 'changedDate', sortable: false },
    { headerName: 'Actor', field: 'actor', sortable: false },
    { headerName: 'Scope', field: 'scopeLabel', sortable: false },
    { headerName: 'Target', field: 'target', sortable: false },
    { headerName: 'Action', field: 'actionLabel', sortable: false },
    { headerName: 'Changes', field: 'changesSummary', sortable: false }
  ];
  readonly networkTableData$ = new BehaviorSubject<CrmClientNetworkRow[]>([]);
  readonly userTableData$ = new BehaviorSubject<CrmClientUserRow[]>([]);
  readonly actionTableData$ = new BehaviorSubject<CrmActionItem[]>([]);
  readonly managedVendorLcTableData$ = new BehaviorSubject<CrmManagedVendorLcRow[]>([]);
  readonly changelogTableData$ = new BehaviorSubject<CrmClientChangeLogRow[]>([]);
  private liveAddLcNetworks: CrmPrimaryNetworkOption[] = [];
  private addLcOptionsRequestId = 0;
  private hasLoadedLiveAddLcOptions = false;
  private isClientDetailsRoute = false;
  private pendingDetailsRequest: { clientId: string; byClientId: boolean } | null = null;
  private deletingContactIds = new Set<number>();
  private completingActionIds = new Set<number>();
  private disregardingActionIds = new Set<number>();
  private modalOverlayMouseDownTarget: EventTarget | null = null;
  readonly printOptions: CrmPrintMenuOption[] = [
    { id: 'details-page-report', label: 'Details Page Report' },
    { id: 'users-report', label: 'Users Report' },
    { id: 'activity-report', label: 'Activity Report' }
  ];

  constructor(private route: ActivatedRoute, private router: Router, private crmService: CrmService) {
  }

  ngOnInit() {
    this.crmUser = this.crmService.getStoredCrmUser();
    if (!this.crmUser) {
      this.isLoadingDetails = false;
    }

    this.route.paramMap.subscribe((paramMap) => {
      const clientId = paramMap.get('clientId');
      const request = {
        clientId: clientId || paramMap.get('networkId') || '',
        byClientId: !!clientId
      };
      this.pendingDetailsRequest = request;

      if (!this.crmUser) {
        this.isLoadingDetails = false;
        this.detailsError = '';
        return;
      }

      if (clientId) {
        this.loadClientDetails(clientId, true);
        return;
      }

      this.loadClientDetails(paramMap.get('networkId') || '', false);
    });
  }

  retryDetailsLoad(): void {
    if (!this.crmUser) {
      return;
    }

    this.loadClientDetails(this.isClientDetailsRoute ? this.clientId : this.networkId, this.isClientDetailsRoute);
  }

  get isDetailedDetailsView(): boolean {
    return this.detailsViewMode === 'detailed';
  }

  get detailsHeaderMetricSkeletonItems(): number[] {
    return this.isDetailedDetailsView
      ? this.detailedDetailsHeaderMetricSkeletonItems
      : this.simpleDetailsHeaderMetricSkeletonItems;
  }

  get detailsInsightSkeletonItems(): number[] {
    return this.isDetailedDetailsView
      ? this.detailedDetailsInsightSkeletonItems
      : this.simpleDetailsInsightSkeletonItems;
  }

  get detailsOpsSkeletonItems(): number[] {
    return this.isDetailedDetailsView
      ? this.detailedDetailsOpsSkeletonItems
      : this.simpleDetailsOpsSkeletonItems;
  }

  get detailsSectionSkeletonItems(): number[] {
    return this.isDetailedDetailsView
      ? this.detailedDetailsSectionSkeletonItems
      : this.simpleDetailsSectionSkeletonItems;
  }

  setDetailsViewMode(mode: CrmDetailsViewMode): void {
    this.detailsViewMode = mode;
    this.detailsViewTabs = this.buildDetailsViewTabs(mode);
    try {
      window.localStorage.setItem(CRM_DASHBOARD_VIEW_MODE_STORAGE_KEY, mode);
    } catch (error) {
      // Ignore localStorage failures; the selected view still applies for this session.
    }

    if (mode === 'simple') {
      this.syncSimpleContactBillingExpansion();
    }
  }

  updateDetailsViewTab(item: TabsNavigationItems): void {
    this.setDetailsViewMode(item && item.name === 'Simple View' ? 'simple' : 'detailed');
  }

  private getStoredDetailsViewMode(): CrmDetailsViewMode {
    try {
      const storedMode = window.localStorage.getItem(CRM_DASHBOARD_VIEW_MODE_STORAGE_KEY);
      return storedMode === 'simple' ? 'simple' : 'detailed';
    } catch (error) {
      return 'detailed';
    }
  }

  private buildDetailsViewTabs(mode: CrmDetailsViewMode): TabsNavigationItems[] {
    return [
      { name: 'Detailed View', icon: 'fa-expand', selected: mode === 'detailed' },
      { name: 'Simple View', icon: 'fa-compress', selected: mode === 'simple' }
    ];
  }

  get crmUserDisplayName(): string {
    if (!this.crmUser) {
      return '';
    }

    const name = `${this.crmUser.first_name || ''} ${this.crmUser.last_name || ''}`.trim();
    return name || this.crmUser.username || this.crmUser.email || `User #${this.crmUser.id}`;
  }

  submitCrmLogin(): void {
    const username = this.crmLoginUsername.trim();
    if (!username || !this.crmLoginPassword) {
      this.crmLoginError = 'Username and password are required.';
      return;
    }

    this.isCrmLoggingIn = true;
    this.crmLoginError = '';
    this.crmService.loginCrmUser(username, this.crmLoginPassword).subscribe(
      (user) => {
        this.crmUser = user;
        this.crmLoginPassword = '';
        this.isCrmLoggingIn = false;
        const request = this.pendingDetailsRequest;
        if (request) {
          this.loadClientDetails(request.clientId, request.byClientId);
        }
      },
      (error) => {
        this.crmLoginError = this.requestErrorMessage(error, 'CRM login failed.');
        this.isCrmLoggingIn = false;
      }
    );
  }

  logoutCrmUser(): void {
    this.crmService.clearStoredCrmUser();
    this.crmService.clearCrmCaches();
    this.crmUser = null;
    this.crmLoginPassword = '';
    this.crmLoginError = '';
    this.detailsError = '';
    this.dropdownDeleteError = '';
    this.activeDetailDropdown = null;
    this.isLoadingDetails = false;
    this.setDetailsSupplementalLoading(false);
    this.detailsEnrichmentRequestId++;
    this.networkTableData$.next([]);
    this.userTableData$.next([]);
    this.managedVendorLcTableData$.next([]);
    this.changelogTableData$.next([]);
  }

  get activeUsersCount(): number {
    return this.users.filter((user) => user.status === 'active').length;
  }

  get inUseTokensTotal(): number {
    if (this.tokenBreakdown && (this.tokenBreakdown.total > 0 || this.tokenBreakdown.byRole.length > 0)) {
      return this.tokenBreakdown.used || 0;
    }

    return this.attachedLcNetworks.reduce((total, network) => total + (network.metrics ? network.metrics.tokens_used : 0), 0);
  }

  get unusedTokensTotal(): number {
    if (this.tokenBreakdown && (this.tokenBreakdown.total > 0 || this.tokenBreakdown.byRole.length > 0)) {
      return this.tokenBreakdown.unused || 0;
    }

    return this.attachedLcNetworks.reduce((total, network) => {
      if (!network.metrics) {
        return total;
      }

      return total + Math.max(network.metrics.tokens_total - network.metrics.tokens_used, 0);
    }, 0);
  }

  get primaryLcCount(): number {
    return this.attachedLcNetworks.filter((network) => network.is_primary).length;
  }

  get primaryLcName(): string | null {
    const primaryNetwork = this.attachedLcNetworks.find((network) => network.is_primary);
    return primaryNetwork ? primaryNetwork.network_name : null;
  }

  get primaryLcId(): number | null {
    const primaryNetwork = this.attachedLcNetworks.find((network) => network.is_primary);
    return primaryNetwork ? primaryNetwork.network_id : null;
  }

  get vendorLcLabel(): string {
    if (!this.vendorLc || this.vendorLc.vendorId === 0) {
      return 'No vendor';
    }

    return this.vendorLc.vendorName ? `${this.vendorLc.vendorName}` : `LC #${this.vendorLc.vendorId}`;
  }

  get vendorLcPrimaryContactLabel(): string {
    if (!this.vendorLc || this.vendorLc.ownerUserId === null) {
      return '-';
    }

    const primaryContactName = this.vendorLc.ownerName || 'Primary Contact';
    return `${primaryContactName} #${this.vendorLc.ownerUserId}`;
  }

  get leaningDataRows(): Array<{ label: string; count: number }> {
    const metrics = this.metrics;
    return [
      { label: 'Active modules', count: metrics ? metrics.module_published_count || 0 : 0 },
      { label: 'Draft modules', count: metrics ? metrics.module_draft_count || 0 : 0 },
      { label: 'Curriculums', count: metrics ? metrics.curriculum_count || 0 : 0 },
      { label: 'Competencies', count: metrics ? metrics.competency_count || 0 : 0 },
      { label: 'Subjects', count: metrics ? metrics.subject_count || 0 : 0 }
    ];
  }

  get tokenBreakdownRows() {
    return this.tokenBreakdown && this.tokenBreakdown.byRole
      ? this.tokenBreakdown.byRole.filter((role) => role.total > 0)
      : [];
  }

  get showManagedVendorLcsSection(): boolean {
    return this.isVendorClient;
  }

  get managedVendorLcCount(): number {
    return this.managedVendorLcs.length;
  }

  get companyProfileDisplayName(): string {
    return this.companyProfile && this.companyProfile.generalName
      ? this.companyProfile.generalName
      : this.displayClientName;
  }

  get companyPrimaryContactName(): string {
    if (this.editDraft) {
      const draftName = `${this.editDraft.primaryContactFirstName || ''} ${this.editDraft.primaryContactLastName || ''}`.trim();
      if (draftName) {
        return draftName;
      }

      if (this.editDraft.primaryContactEmail) {
        return this.editDraft.primaryContactEmail;
      }
    }

    return this.primaryContact ? this.getContactDisplayName(this.primaryContact) : '-';
  }

  get companyTagsLabel(): string {
    return this.draftTagList.length > 0 ? this.draftTagList.join(', ') : '-';
  }

  get companyNotesLabel(): string {
    return (this.summary && this.summary.notesSummary) || (this.editDraft && this.editDraft.notesSummary) || '-';
  }

  get lcFeatureSummary(): string {
    if (!this.network) {
      return '-';
    }

    return [
      `API ${this.network.apiEnabled ? 'On' : 'Off'}`,
      `Express ${this.network.expressEnabled ? 'On' : 'Off'}`,
      `SMS ${this.network.enableSms ? 'On' : 'Off'}`,
      `WhatsApp ${this.network.enableWhatsapp ? 'On' : 'Off'}`,
      `2FA ${this.network.enable_2fa ? 'On' : 'Off'}`
    ].join(' / ');
  }

  get primaryLcContactLabel(): string {
    if (!this.primaryLcContact || !this.primaryLcContact.name) {
      return '-';
    }

    return this.primaryLcContact.email
      ? `${this.primaryLcContact.name} / ${this.primaryLcContact.email}`
      : this.primaryLcContact.name;
  }

  get networkInfoEntries(): CrmNetworkInfoEntry[] {
    if (!this.networkInfo) {
      return [];
    }

    return [
      { label: 'Network ID', value: this.networkInfo.networkID },
      { label: 'Subscription Start', value: this.networkInfo.subscription_start_date, isDate: true },
      { label: 'Subscription End', value: this.networkInfo.subscription_end_date, isDate: true },
      { label: 'Paid Until', value: this.networkInfo.paid_until_date, isDate: true },
      { label: 'Next Invoice', value: this.networkInfo.next_invoice_date, isDate: true },
      { label: 'Next Payment Due', value: this.networkInfo.next_payment_due_date, isDate: true },
      { label: 'Reminder Date', value: this.networkInfo.reminder_date, isDate: true },
      { label: 'Reminder Repeat Days', value: this.networkInfo.reminder_repeat_days },
      { label: 'Email Reminder', value: this.networkInfo.email_reminder },
      { label: 'Advanced Payment', value: this.networkInfo.advanced_payment },
      { label: 'Reference', value: this.networkInfo.reference, stacked: true },
      { label: 'Notes', value: this.networkInfo.notes, stacked: true }
    ];
  }

  private getDefaultNetworkInfoDraft(): CrmNetworkInfoDraft {
    return {
      subscriptionStart: '',
      subscriptionEnd: '',
      paidUntil: '',
      nextInvoice: '',
      nextPaymentDue: '',
      sendOn: '',
      repeatDays: 0,
      emailReminder: false,
      advancedPayment: false,
      reference: '',
      notes: ''
    };
  }

  toggleEditNetworkInfo(): void {
    if (this.isEditingNetworkInfo) {
      this.cancelEditNetworkInfo();
      return;
    }

    this.startEditNetworkInfo();
  }

  startEditNetworkInfo(): void {
    if (!this.networkInfo) {
      return;
    }

    this.networkInfoDraft = {
      subscriptionStart: this.formatDateInputValue(this.networkInfo.subscription_start_date),
      subscriptionEnd: this.formatDateInputValue(this.networkInfo.subscription_end_date),
      paidUntil: this.formatDateInputValue(this.networkInfo.paid_until_date),
      nextInvoice: this.formatDateInputValue(this.networkInfo.next_invoice_date),
      nextPaymentDue: this.formatDateInputValue(this.networkInfo.next_payment_due_date),
      sendOn: this.formatDateInputValue(this.networkInfo.reminder_date),
      repeatDays: this.networkInfo.reminder_repeat_days || 0,
      emailReminder: !!this.networkInfo.email_reminder,
      advancedPayment: !!this.networkInfo.advanced_payment,
      reference: this.networkInfo.reference || '',
      notes: this.networkInfo.notes || ''
    };
    this.networkInfoError = '';
    this.isEditingNetworkInfo = true;
  }

  cancelEditNetworkInfo(): void {
    this.isEditingNetworkInfo = false;
    this.networkInfoError = '';
  }

  saveNetworkInfo(): void {
    const networkInfo = this.networkInfo;
    if (!networkInfo) {
      return;
    }

    const companyId = Number(this.clientId) || 0;
    if (companyId <= 0) {
      this.networkInfoError = 'This record cannot be saved because it is missing a CRM company id.';
      return;
    }

    this.networkInfoError = '';
    this.isSavingNetworkInfo = true;

    const draft = this.networkInfoDraft;
    const repeatDays = Math.max(0, Number(draft.repeatDays) || 0);

    this.crmService.updateClient(companyId, {
      lc_info: {
        network_id: networkInfo.networkID,
        subscription_start_date: this.nullIfEmpty(draft.subscriptionStart),
        subscription_end_date: this.nullIfEmpty(draft.subscriptionEnd),
        paid_until_date: this.nullIfEmpty(draft.paidUntil),
        next_invoice_date: this.nullIfEmpty(draft.nextInvoice),
        next_payment_due_date: this.nullIfEmpty(draft.nextPaymentDue),
        reminder_date: this.nullIfEmpty(draft.sendOn),
        reminder_repeat_days: repeatDays,
        email_reminder: draft.emailReminder ? 1 : 0,
        advanced_payment: draft.advancedPayment ? 1 : 0,
        reference: this.nullIfEmpty(draft.reference),
        notes: this.nullIfEmpty(draft.notes)
      }
    }).subscribe(
      () => {
        this.networkInfo = {
          ...networkInfo,
          subscription_start_date: draft.subscriptionStart || '',
          subscription_end_date: draft.subscriptionEnd || '',
          paid_until_date: draft.paidUntil || '',
          next_invoice_date: draft.nextInvoice || '',
          next_payment_due_date: draft.nextPaymentDue || '',
          reminder_date: draft.sendOn || '',
          reminder_repeat_days: repeatDays,
          email_reminder: draft.emailReminder ? 1 : 0,
          advanced_payment: draft.advancedPayment ? 1 : 0,
          reference: this.nullIfEmpty(draft.reference),
          notes: this.nullIfEmpty(draft.notes)
        };
        this.isEditingNetworkInfo = false;
        this.isSavingNetworkInfo = false;
      },
      (error) => {
        this.networkInfoError = this.requestErrorMessage(error, 'Unable to save subscription details.');
        this.isSavingNetworkInfo = false;
      }
    );
  }

  get attachedLcNetworks(): CrmClientNetwork[] {
    return this.networks.filter((network) => this.hasAttachedLc(network));
  }

  get attachedLcCount(): number {
    return this.attachedLcNetworks.length;
  }

  get hasLcDetails(): boolean {
    return this.attachedLcCount > 0 || !!this.network;
  }

  get prospectWithoutLcCount(): number {
    return this.networks.length - this.attachedLcCount;
  }

  get lcTotalBreakdown(): CrmBreakdownItem[] {
    return [
      {
        key: 'total-lcs',
        label: 'Total LCs',
        count: this.attachedLcCount
      },
      {
        key: 'primary-lcs',
        label: 'Primary LCs',
        count: this.primaryLcCount
      },
      {
        key: 'secondary-lcs',
        label: 'Secondary LCs',
        count: Math.max(this.attachedLcCount - this.primaryLcCount, 0)
      },
      {
        key: 'prospects-without-lc',
        label: 'Prospects Without LC',
        count: this.prospectWithoutLcCount
      }
    ];
  }

  get lcStageBreakdown(): CrmBreakdownItem[] {
    return this.buildDetailsBreakdown(
      this.networks.map((network) => this.getNetworkClientStage(network)),
      ['prospect', 'proposal', 'trial', 'client', 'at_risk', 'paused', 'lost']
    );
  }

  get lcTypeBreakdown(): CrmBreakdownItem[] {
    return [
      ...this.buildDetailsBreakdown(
        this.networks
          .filter((network) => this.hasAttachedLc(network))
          .map((network) => this.getNetworkClientType(network)),
        ['lead', 'customer', 'partner', 'internal']
      ),
      {
        key: 'without-lc',
        label: 'Without LC',
        count: this.prospectWithoutLcCount
      }
    ];
  }

  get lcCrmStatusBreakdown(): CrmBreakdownItem[] {
    return this.buildDetailsBreakdown(
      this.networks.map((network) => this.hasAttachedLc(network) ? network.crm_status || 'missing' : 'no-lc'),
      ['trial', 'workplace', 'trainers', 'hybrid', 'vendor', 'no-lc', 'missing']
    );
  }

  get lcTestModeBreakdown(): CrmBreakdownItem[] {
    if (this.networks.length === 0) {
      return [];
    }

    return [
      {
        key: 'test-mode-enabled',
        label: 'Enabled',
        count: this.attachedLcNetworks.filter((network) => network.test_mode).length
      },
      {
        key: 'test-mode-off',
        label: 'Off',
        count: this.attachedLcNetworks.filter((network) => !network.test_mode).length
      },
      {
        key: 'test-mode-no-lc',
        label: 'No LC',
        count: this.prospectWithoutLcCount
      }
    ];
  }

  get selectedAddLcCount(): number {
    return this.selectedAddLcNetworkIds.length;
  }

  get selectedAddLcSummary(): string | null {
    const selectedNames = this.selectedAddLcNetworkIds
      .map((networkId) => this.getSelectedAddLcOption(networkId))
      .filter((option): option is CrmPrimaryNetworkOption => !!option)
      .map((option) => option.network_name);

    if (selectedNames.length === 0) {
      return null;
    }

    return selectedNames.join(', ');
  }

  get isEditingClientLc(): boolean {
    return this.editingClientNetworkRow !== null;
  }

  get clientModalTitle(): string {
    return this.isEditingClientLc ? 'Edit LC Link' : 'Add LC Link';
  }

  get clientModalDescription(): string {
    return this.isEditingClientLc
      ? 'Update this record and its selected LCs.'
      : 'Attach an LC to this record.';
  }

  get clientModalLcDescription(): string {
    return this.isEditingClientLc
      ? 'Choose one or more LCs, keep the current selections, or clear them for a prospect without an assigned LC.'
      : 'Choose the LC to attach to this record.';
  }

  get clientModalSubmitLabel(): string {
    return this.isEditingClientLc ? 'Save Changes' : 'Attach LC';
  }

  get addLcsRequiresPrimary(): boolean {
    return this.attachedLcCount === 0 || !this.attachedLcNetworks.some((network) => network.is_primary);
  }

  get filteredAddLcOptions(): CrmPrimaryNetworkOption[] {
    const searchTerm = this.addLcSearchTerm.trim().toLowerCase();

    if (!searchTerm) {
      return this.addLcOptions;
    }

    return this.addLcOptions.filter((option) => [
      option.network_name,
      String(option.network_id),
      option.crm_status,
      option.notes || '',
      option.vendor ? 'vendor' : ''
    ].some((value) => value.toLowerCase().includes(searchTerm)));
  }

  get totalAddLcOptionPages(): number {
    return Math.max(Math.ceil(this.filteredAddLcOptions.length / this.addLcOptionsPerPage), 1);
  }

  get currentAddLcOptionsPage(): number {
    return Math.min(this.addLcOptionsPage, this.totalAddLcOptionPages);
  }

  get paginatedAddLcOptions(): CrmPrimaryNetworkOption[] {
    const start = (this.currentAddLcOptionsPage - 1) * this.addLcOptionsPerPage;
    return this.filteredAddLcOptions.slice(start, start + this.addLcOptionsPerPage);
  }

  get addLcOptionsRangeStart(): number {
    return this.filteredAddLcOptions.length === 0 ? 0 : (this.currentAddLcOptionsPage - 1) * this.addLcOptionsPerPage + 1;
  }

  get addLcOptionsRangeEnd(): number {
    return Math.min(this.currentAddLcOptionsPage * this.addLcOptionsPerPage, this.filteredAddLcOptions.length);
  }

  get canAddSelectedLcs(): boolean {
    return this.addClientName.trim().length > 0 && (this.isEditingClientLc || this.selectedAddLcNetworkIds.length === 1);
  }

  get filteredNetworkTableRows(): CrmClientNetworkRow[] {
    const searchTerm = this.networkSearchTerm.trim().toLowerCase();

    return this.allNetworkTableRows.filter((row) =>
      (this.selectedNetworkStage === 'all' || row.clientStage === this.selectedNetworkStage)
      && (this.selectedNetworkType === 'all' || row.clientType === this.selectedNetworkType)
      && (!searchTerm || this.getNetworkSearchValues(row).some((value) => value.toLowerCase().includes(searchTerm)))
    );
  }

  get totalNetworkPages(): number {
    return Math.max(Math.ceil(this.filteredNetworkTableRows.length / this.networkItemsPerPage), 1);
  }

  get networkRangeStart(): number {
    return this.filteredNetworkTableRows.length === 0 ? 0 : (this.currentNetworkPage - 1) * this.networkItemsPerPage + 1;
  }

  get networkRangeEnd(): number {
    return Math.min(this.currentNetworkPage * this.networkItemsPerPage, this.filteredNetworkTableRows.length);
  }

  get displayClientName(): string {
    return (this.editDraft ? this.editDraft.clientName : this.clientName) || 'Untitled Record';
  }

  get billingContactStatusLabel(): string {
    const name = this.editDraft
      ? this.editDraft.billingContactName
      : (this.billing ? this.billing.billingContactName || '' : '');

    return `${name || ''}`.trim() || 'MISSING';
  }

  get billingSyncStatus(): CrmBillingSyncStatus {
    const sourceStatus = this.billing ? this.billing.syncStatus : 'missing';
    if (sourceStatus !== 'missing') {
      return sourceStatus;
    }

    const hasName = this.hasBillingFieldValue('billingContactName');
    const hasRoute = this.hasBillingFieldValue('billingEmail') || this.hasBillingFieldValue('billingPhone');
    if (hasName && hasRoute) {
      return 'healthy';
    }

    return hasName || hasRoute ? 'warning' : 'missing';
  }

  private hasBillingFieldValue(field: 'billingContactName' | 'billingEmail' | 'billingPhone'): boolean {
    const draftValue = this.editDraft ? this.editDraft[field] : '';
    const billingValue = this.billing ? this.billing[field] || '' : '';
    return `${draftValue || billingValue || ''}`.trim().length > 0;
  }

  get userChangeCount(): number {
    return this.changelog.filter((entry) => entry.scope === 'user').length;
  }

  get recentNotesCount(): number {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - 30);

    return this.notes.filter((note) => new Date(note.created) >= cutoffDate).length;
  }

  get pinnedNotes(): CrmClientNote[] {
    return this.notes.filter((note) => note.pinned);
  }

  get sortedNotes(): CrmClientNote[] {
    return [...this.notes].sort((first, second) => {
      if (first.pinned !== second.pinned) {
        return first.pinned ? -1 : 1;
      }

      return this.dateSortValue(second.created) - this.dateSortValue(first.created);
    });
  }

  get paginatedActivities(): CrmClientActivity[] {
    const start = (this.currentActivityPage - 1) * this.activityItemsPerPage;
    return this.activities.slice(start, start + this.activityItemsPerPage);
  }

  get totalActivityPages(): number {
    return Math.max(Math.ceil(this.activities.length / this.activityItemsPerPage), 1);
  }

  get activityRangeStart(): number {
    return this.activities.length === 0 ? 0 : (this.currentActivityPage - 1) * this.activityItemsPerPage + 1;
  }

  get activityRangeEnd(): number {
    return Math.min(this.currentActivityPage * this.activityItemsPerPage, this.activities.length);
  }

  get canSaveManualNote(): boolean {
    return !this.isSavingManualNote
      && this.manualNoteDraft.entityType.trim().length > 0
      && this.getManualLogEntityId(this.manualNoteDraft.entityType) > 0
      && this.manualNoteDraft.body.trim().length > 0;
  }

  get canSaveEditNote(): boolean {
    return !this.isSavingEditNote
      && !!this.editNoteDraft
      && this.editNoteDraft.id > 0
      && this.editNoteDraft.entityType.trim().length > 0
      && this.editNoteDraft.body.trim().length > 0;
  }

  get canSaveManualActivity(): boolean {
    return !this.isSavingManualActivity
      && this.manualActivityDraft.entityType.trim().length > 0
      && this.getManualLogEntityId(this.manualActivityDraft.entityType) > 0
      && this.manualActivityDraft.created.trim().length > 0
      && this.manualActivityDraft.action.trim().length > 0
      && this.manualActivityDraft.details.trim().length > 0;
  }

  get activeClientActionsCount(): number {
    return this.actions.filter((action) => !this.isClientActionClosed(action)).length;
  }

  get completedClientActionsCount(): number {
    return this.actions.filter((action) => this.isClientActionClosed(action)).length;
  }

  get canSaveClientAction(): boolean {
    return !this.isSavingClientAction
      && this.manualActionDraft.level.trim().length > 0
      && this.manualActionDraft.action.trim().length > 0
      && this.manualActionDraft.actionDate.trim().length > 0
      && this.manualActionDraft.dueDate.trim().length > 0;
  }

  get draftTagList(): string[] {
    return this.splitCsv(this.editDraft ? this.editDraft.tagsCsv : '');
  }

  get primaryContactRoleList(): string[] {
    return this.splitCsv(this.editDraft ? this.editDraft.primaryContactRolesCsv : '');
  }

  get primaryContactTagList(): string[] {
    return this.splitCsv(this.editDraft ? this.editDraft.primaryContactTagsCsv : '');
  }

  onDetailTagDropdownFocus(target: CrmDetailTagDropdownTarget): void {
    this.activeDetailTagDropdown = target;
  }

  onDetailTagDropdownInput(target: CrmDetailTagDropdownTarget): void {
    this.activeDetailTagDropdown = target;
    if (target !== 'additionalContactTags') {
      this.onEditDraftChange();
    }
  }

  onDetailTagDropdownBlur(target: CrmDetailTagDropdownTarget): void {
    window.setTimeout(() => {
      if (this.activeDetailTagDropdown === target) {
        this.activeDetailTagDropdown = null;
      }
    }, 120);
  }

  selectDetailTagDropdownItem(target: CrmDetailTagDropdownTarget, item: string): void {
    const nextValue = this.replaceCurrentCsvTag(this.detailTagDropdownValue(target), item);
    this.setDetailTagDropdownValue(target, nextValue);
    this.activeDetailTagDropdown = null;
  }

  deleteDetailTagDropdownItem(target: CrmDetailTagDropdownTarget, item: string, event: MouseEvent): void {
    this.deleteDetailDropdownItem(this.detailTagDropdownName(target), item, event);
  }

  isDeletingDetailTagDropdownItem(target: CrmDetailTagDropdownTarget, item: string): boolean {
    return this.isDeletingDetailDropdownItem(this.detailTagDropdownName(target), item);
  }

  onDetailDropdownFocus(target: CrmDetailDropdownTarget): void {
    this.activeDetailDropdown = target;
  }

  onDetailDropdownInput(target: CrmDetailDropdownTarget): void {
    this.activeDetailDropdown = target;
    this.onEditDraftChange();
  }

  onDetailDropdownBlur(target: CrmDetailDropdownTarget): void {
    window.setTimeout(() => {
      if (this.activeDetailDropdown === target) {
        this.activeDetailDropdown = null;
      }
    }, 120);
  }

  selectDetailDropdownItem(target: CrmDetailDropdownTarget, item: string): void {
    if (!this.editDraft) {
      return;
    }

    this.editDraft[target] = item;
    this.onEditDraftChange();
    this.activeDetailDropdown = null;
  }

  deleteDetailDropdownItem(name: keyof CrmDropdownOptions, item: string, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const deleteKey = this.dropdownDeleteKey(name, item);
    if (this.deletingDetailDropdownItems.has(deleteKey)) {
      return;
    }

    this.dropdownDeleteError = '';
    this.deletingDetailDropdownItems.add(deleteKey);
    this.crmService.deleteDropdownItem(name, item).subscribe(
      (response) => {
        this.crmDropdowns = response.dropdowns;
        this.deletingDetailDropdownItems.delete(deleteKey);
      },
      (error) => {
        this.dropdownDeleteError = this.requestErrorMessage(error, 'Unable to delete dropdown item.');
        this.deletingDetailDropdownItems.delete(deleteKey);
      }
    );
  }

  isDeletingDetailDropdownItem(name: keyof CrmDropdownOptions, item: string): boolean {
    return this.deletingDetailDropdownItems.has(this.dropdownDeleteKey(name, item));
  }

  getDetailDropdownSuggestions(target: CrmDetailDropdownTarget): string[] {
    const rawValue = this.editDraft ? String(this.editDraft[target] || '') : '';
    const value = rawValue.trim().toLowerCase();
    const options = this.crmDropdowns[target] || [];
    const matches = value
      ? options.filter((option) => option.toLowerCase().includes(value))
      : options;

    return matches.slice(0, 8);
  }

  getDetailTagDropdownSuggestions(target: CrmDetailTagDropdownTarget): string[] {
    const rawValue = this.detailTagDropdownValue(target);
    const value = this.currentCsvTagSegment(rawValue).toLowerCase();
    const dropdownName = this.detailTagDropdownName(target);
    const selectedTags = new Set(this.splitCsv(rawValue).map((tag) => tag.toLowerCase()));
    const options = this.crmDropdowns[dropdownName] || [];
    const matches = value
      ? options.filter((option) => option.toLowerCase().includes(value))
      : options;

    return matches
      .filter((option) => !selectedTags.has(option.toLowerCase()))
      .slice(0, 8);
  }

  get primaryContactRecordLabel(): string {
    const contactName = this.editDraft ? this.editDraft.primaryContactFirstName.trim() + " " + this.editDraft.primaryContactLastName.trim() : '';
    return contactName.trim() ? contactName.trim() : 'MISSING';
  }

  private buildContactTableRows(): CrmContactTableRow[] {
    const rows: CrmContactTableRow[] = [];
    const primaryContact = this.buildPrimaryContactTableRow();
    const billingContact = this.buildBillingContactTableRow();

    if (primaryContact) {
      rows.push(primaryContact);
    }

    if (billingContact) {
      rows.push(billingContact);
    }

    this.additionalContactPersons.forEach((contact) => {
      rows.push(this.buildContactTableRow(contact, `additional:${contact.id}`, '', true));
    });

    return rows;
  }

  get additionalContactPersons(): CrmContactPerson[] {
    return this.contactPersons.filter((contact) =>
      !this.isPrimaryContact(contact) && !this.isBillingContact(contact)
    );
  }

  get canSaveAdditionalContact(): boolean {
    const draft = this.additionalContactDraft;
    return !this.isSavingAdditionalContact
      && (draft.firstName.trim().length > 0 || draft.lastName.trim().length > 0);
  }

  get isEditingAdditionalContact(): boolean {
    return Number(this.editingAdditionalContactId || 0) > 0;
  }

  get editingAdditionalContact(): CrmContactPerson | null {
    const editingContactId = Number(this.editingAdditionalContactId || 0);
    if (editingContactId <= 0) {
      return null;
    }

    return this.contactPersons.find((contact) => contact.id === editingContactId) || null;
  }

  get additionalContactModalTitle(): string {
    return this.isEditingAdditionalContact ? 'Edit contact' : 'Add contact';
  }

  get additionalContactModalDescription(): string {
    return this.isEditingAdditionalContact
      ? 'Update this additional contact. Primary and billing contacts are managed in their own sections.'
      : 'Create an additional contact for this record. Primary and billing contacts are managed in their own sections.';
  }

  get additionalContactSubmitLabel(): string {
    if (this.isSavingAdditionalContact) {
      return 'Saving...';
    }

    return this.isEditingAdditionalContact ? 'Save contact' : 'Add contact';
  }

  get currentLcLink(): CrmClientNetwork | null {
    const currentNetworkId = Number(this.networkId || (this.network ? this.network.networkID : 0)) || 0;
    if (currentNetworkId > 0) {
      const matchingLink = this.networks.find((network) =>
        this.hasAttachedLc(network) && network.network_id === currentNetworkId
      );
      if (matchingLink) {
        return matchingLink;
      }
    }

    return this.networks.find((network) => this.hasAttachedLc(network)) || null;
  }

  get canRemoveLcLink(): boolean {
    const currentLink = this.currentLcLink;
    return !!currentLink
      && currentLink.id > 0
      && currentLink.company_id > 0;
  }

  isDeletingContact(contact: CrmContactPerson): boolean {
    return this.deletingContactIds.has(contact.id);
  }

  goToDashboard(): void {
    this.router.navigate(['/crm']);
  }

  onDuplicateSearchChange(): void {
    this.crmService.searchDuplicateRecords(this.duplicateSearchTerm).subscribe((results) => {
      this.duplicateSearchResults = results;
    });
  }

  isActionDue(action: CrmActionItem): boolean {
    return !this.isClientActionClosed(action) && new Date(action.actionDate) <= new Date();
  }

  isDateDue(value: string | null | undefined): boolean {
    return !!value && new Date(value) <= new Date();
  }

  linkifyText(value: string | null | undefined): string {
    const escaped = this.escapeHtml(value || '-');
    const linkedUrls = escaped.replace(
      /(https?:\/\/[^\s<]+)/g,
      '<a href="$1" target="_blank" rel="noopener">$1</a>'
    );

    return linkedUrls.replace(
      /(^|[\s>])([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/gi,
      '$1<a href="mailto:$2">$2</a>'
    );
  }

  goToLcData(networkId: number): void {
    if (networkId <= 0) {
      return;
    }

    this.router.navigate(['/crm/details', networkId]);
  }

  openRemoveLcLinkConfirmModal(): void {
    const currentLink = this.currentLcLink;
    if (!currentLink || currentLink.id <= 0 || currentLink.company_id <= 0) {
      this.removeLcLinkError = 'This LC link cannot be removed because it is missing a CRM link record.';
      return;
    }

    if (this.hasPendingChanges) {
      this.removeLcLinkError = 'Save or close current edits before removing the LC link.';
      return;
    }

    this.removeLcLinkError = '';
    this.showRemoveLcLinkConfirmModal = true;
  }

  closeRemoveLcLinkConfirmModal(): void {
    if (this.isRemovingLcLink) {
      return;
    }

    this.showRemoveLcLinkConfirmModal = false;
  }

  onModalOverlayMouseDown(event: MouseEvent): void {
    this.modalOverlayMouseDownTarget = event.target;
  }

  shouldCloseModalFromOverlayClick(event: MouseEvent): boolean {
    const shouldClose = this.modalOverlayMouseDownTarget === event.currentTarget
      && event.target === event.currentTarget;
    this.modalOverlayMouseDownTarget = null;

    return shouldClose;
  }

  removeCurrentLcLink(): void {
    const currentLink = this.currentLcLink;
    if (!currentLink || currentLink.id <= 0 || currentLink.company_id <= 0) {
      this.removeLcLinkError = 'This LC link cannot be removed because it is missing a CRM link record.';
      this.showRemoveLcLinkConfirmModal = false;
      return;
    }

    if (this.hasPendingChanges) {
      this.removeLcLinkError = 'Save or close current edits before removing the LC link.';
      this.showRemoveLcLinkConfirmModal = false;
      return;
    }

    if (this.isRemovingLcLink) {
      return;
    }

    const companyId = currentLink.company_id;
    this.removeLcLinkError = '';
    this.isRemovingLcLink = true;

    this.crmService.updateClient(companyId, {
      linked_networks: [{
        ...currentLink,
        _delete: true
      }],
      primary_network: null
    }).subscribe(
      () => {
        this.isRemovingLcLink = false;
        this.showRemoveLcLinkConfirmModal = false;
        this.networks = this.networks.filter((network) => network.id !== currentLink.id);
        this.refreshTables();
        this.router.navigate(['/crm/details/client', companyId]);
      },
      (error) => {
        this.removeLcLinkError = this.requestErrorMessage(error, 'Unable to remove the LC link.');
        this.isRemovingLcLink = false;
      }
    );
  }

  openAddLcsModal(): void {
    this.editingClientNetworkRow = null;
    this.showAddLcsModal = true;
    this.addLcsError = '';
    this.addClientName = this.displayClientName;
    this.addClientStage = this.summary ? this.summary.stage : 'client';
    this.addClientType = this.summary && this.summary.type ? this.summary.type : 'customer';
    this.addLcSearchTerm = '';
    this.selectedAddLcNetworkIds = [];
    this.addLcOptionsPage = 1;
    this.selectedLcContactNetwork = null;
    this.selectedLcContactEntries = [];
    this.prepareAddLcOptionsForModal();
  }

  openEditClientModal(row: CrmClientNetworkRow): void {
    this.editingClientNetworkRow = row;
    this.showAddLcsModal = true;
    this.addLcsError = '';
    this.addClientName = row.clientName;
    this.addClientStage = row.clientStage;
    this.addClientType = row.clientType;
    this.addLcSearchTerm = '';
    this.selectedAddLcNetworkIds = this.getClientNetworksForName(row.clientName)
      .filter((network) => this.hasAttachedLc(network))
      .map((network) => network.network_id);
    this.addLcOptionsPage = 1;
    this.selectedLcContactNetwork = null;
    this.selectedLcContactEntries = [];
    this.prepareAddLcOptionsForModal();
  }

  closeAddLcsModal(): void {
    this.showAddLcsModal = false;
    this.addLcsError = '';
    this.editingClientNetworkRow = null;
  }

  onAddLcSearchChange(): void {
    this.addLcOptionsPage = 1;
  }

  goToAddLcOptionsPage(page: number): void {
    this.addLcOptionsPage = Math.max(1, Math.min(page, this.totalAddLcOptionPages));
  }

  isAddLcSelected(networkId: number): boolean {
    return this.selectedAddLcNetworkIds.includes(networkId);
  }

  toggleAddLcSelection(networkId: number): void {
    this.onAddLcToggle(networkId, !this.isAddLcSelected(networkId));
  }

  selectAddLcOption(networkId: number): void {
    this.onAddLcToggle(networkId, true);
  }

  onAddLcToggle(networkId: number, checked: boolean): void {
    this.addLcsError = '';

    const selectedNetworkIds = new Set(this.selectedAddLcNetworkIds);

    if (checked) {
      if (!this.isEditingClientLc) {
        this.selectedAddLcNetworkIds = [networkId];
        return;
      }
      selectedNetworkIds.add(networkId);
    } else {
      selectedNetworkIds.delete(networkId);
    }

    this.selectedAddLcNetworkIds = Array.from(selectedNetworkIds);
  }

  onAddLcOptionClick(networkId: number): void {
    this.toggleAddLcSelection(networkId);
  }

  clearAddLcSelection(): void {
    this.addLcsError = '';
    this.selectedAddLcNetworkIds = [];
  }

  addSelectedLcs(): void {
    this.addLcsError = '';
    const clientName = this.addClientName.trim();

    if (!clientName) {
      this.addLcsError = 'Enter a record name.';
      return;
    }

    if (this.selectedAddLcNetworkIds.length !== 1) {
      this.addLcsError = 'Select one LC to attach.';
      return;
    }

    const selectedOptions = this.selectedAddLcNetworkIds
      .map((networkId) => this.getSelectedAddLcOption(networkId))
      .filter((option): option is CrmPrimaryNetworkOption => !!option);

    if (selectedOptions.length !== this.selectedAddLcNetworkIds.length) {
      this.addLcsError = 'The selected LC is no longer available. Refresh the list and try again.';
      return;
    }

    const nextLinkId = this.crmService.getNextCompanyNetworkLinkId();
    const shouldMakeFirstSelectedPrimary = selectedOptions.length > 0 && this.addLcsRequiresPrimary;
    const newLinks = selectedOptions
      .map((selectedOption, index) => this.createClientNetworkLink(
        selectedOption,
        shouldMakeFirstSelectedPrimary && index === 0,
        nextLinkId + index,
        clientName,
        this.addClientStage,
        this.addClientType
      ));
    const existingNetworks = shouldMakeFirstSelectedPrimary
      ? this.networks.map((network) => ({ ...network, is_primary: false }))
      : this.networks;

    const nextNetworks = this.normalizeClientNetworks([...existingNetworks, ...newLinks]);
    const attachedNetworkId = newLinks[0] ? newLinks[0].network_id : 0;
    this.saveClientNetworkLinks(nextNetworks, () => {
      this.closeAddLcsModal();
      if (attachedNetworkId > 0) {
        this.router.navigate(['/crm/details', attachedNetworkId]);
      }
    }, this.buildClientLinkFormUpdates(clientName));
  }

  saveClientModal(): void {
    if (this.editingClientNetworkRow) {
      this.updateSelectedClientLc();
      return;
    }

    this.addSelectedLcs();
  }

  updateSelectedClientLc(): void {
    if (!this.editingClientNetworkRow) {
      return;
    }

    this.addLcsError = '';
    const clientName = this.addClientName.trim();

    if (!clientName) {
      this.addLcsError = 'Enter a record name.';
      return;
    }

    const currentNetwork = this.editingClientNetworkRow.network;
    const currentClientNetworks = this.getClientNetworksForName(this.editingClientNetworkRow.clientName);
    const currentClientNetworkIds = new Set(currentClientNetworks.map((network) => network.id));
    const selectedOptions = this.selectedAddLcNetworkIds
      .map((networkId) => this.getSelectedAddLcOption(networkId))
      .filter((option): option is CrmPrimaryNetworkOption => !!option);

    if (selectedOptions.length !== this.selectedAddLcNetworkIds.length) {
      this.addLcsError = 'The selected LC is no longer available. Refresh the list and try again.';
      return;
    }

    const now = this.formatTimestamp(new Date());
    const existingNetworksByNetworkId = new Map(currentClientNetworks
      .filter((network) => this.hasAttachedLc(network))
      .map((network) => [network.network_id, network]));

    if (selectedOptions.length === 0) {
      const prospectNetwork: CrmClientNetwork = {
        ...currentNetwork,
        network_id: 0,
        network_name: '',
        client_name: clientName,
        client_stage: this.addClientStage,
        client_type: this.addClientType,
        is_primary: false,
        vendor: false,
        test_mode: false,
        test_mode_note: null,
        crm_status: null,
        notes: 'Prospect without an assigned LC.',
        modified: now,
        metrics: null,
        primary_contact: null
      };
      this.networks = this.normalizeClientNetworks([
        ...this.networks.filter((network) => !currentClientNetworkIds.has(network.id)),
        prospectNetwork
      ]);
      this.persistClientNetworkLinks(this.networks, this.buildClientLinkFormUpdates(clientName));
      this.refreshTables();
      this.closeAddLcsModal();
      return;
    }

    let nextLinkId = this.crmService.getNextCompanyNetworkLinkId();
    const selectedNetworks = selectedOptions.map((selectedOption) => {
      const existingNetwork = existingNetworksByNetworkId.get(selectedOption.network_id) || null;

      if (existingNetwork) {
        return {
          ...existingNetwork,
          network_name: selectedOption.network_name,
          client_name: clientName,
          client_stage: this.addClientStage,
          client_type: this.addClientType,
          vendor: selectedOption.vendor,
          test_mode: selectedOption.test_mode,
          test_mode_note: selectedOption.test_mode_note,
          crm_status: selectedOption.crm_status,
          notes: existingNetwork.notes,
          modified: now
        };
      }

      return this.createClientNetworkLink(
        selectedOption,
        false,
        nextLinkId++,
        clientName,
        this.addClientStage,
        this.addClientType
      );
    });
    this.networks = this.normalizeClientNetworks([
      ...this.networks.filter((network) => !currentClientNetworkIds.has(network.id)),
      ...selectedNetworks
    ]);
    this.persistClientNetworkLinks(this.networks, this.buildClientLinkFormUpdates(clientName));
    this.refreshTables();
    this.closeAddLcsModal();
  }

  onAddLcControlsClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  openLcContactModal(row: CrmClientNetworkRow): void {
    if (!row.hasAttachedLc) {
      return;
    }

    this.selectedLcContactNetwork = row.network;
    this.selectedLcContactEntries = this.getLcPrimaryContactEntries(row.primaryContact);
  }

  closeLcContactModal(): void {
    this.selectedLcContactNetwork = null;
    this.selectedLcContactEntries = [];
  }

  onNetworkSearchChange(): void {
    this.currentNetworkPage = 1;
    this.refreshNetworkTablePage();
  }

  onNetworkFilterChange(): void {
    this.currentNetworkPage = 1;
    this.refreshNetworkTablePage();
  }

  onNetworkClientStageChange(row: CrmClientNetworkRow, stage: CrmCompanyStage): void {
    this.updateNetworkClientCrmFields(row, { client_stage: stage });
  }

  onNetworkClientTypeChange(row: CrmClientNetworkRow, type: CrmCompanyType): void {
    this.updateNetworkClientCrmFields(row, { client_type: type });
  }

  goToNetworkPage(page: number): void {
    this.currentNetworkPage = Math.min(Math.max(page, 1), this.totalNetworkPages);
    this.refreshNetworkTablePage();
  }

  updateNetworkItemsPerPage(itemsPerPage: number | string): void {
    const nextPageSize = Number(itemsPerPage);
    this.networkItemsPerPage = this.networkPageSizeOptions.includes(nextPageSize) ? nextPageSize : this.networkPageSizeOptions[0];
    this.goToNetworkPage(1);
  }

  updateNetworkTableRows(rows: CrmClientNetworkRow[]): void {
    setTimeout(() => {
      this.networkTableRows = rows || [];
    });
  }

  updateUserTableRows(rows: CrmClientUserRow[]): void {
    setTimeout(() => {
      this.userTableRows = rows || [];
    });
  }

  updateManagedVendorLcTableRows(rows: CrmManagedVendorLcRow[]): void {
    setTimeout(() => {
      this.managedVendorLcRows = rows || [];
    });
  }

  updateChangelogTableRows(rows: CrmClientChangeLogRow[]): void {
    setTimeout(() => {
      this.changelogTableRows = rows || [];
    });
  }

  openAddNoteModal(): void {
    this.manualNoteDraft = this.getDefaultManualNoteDraft();
    this.addNoteError = '';
    this.showAddNoteModal = true;
  }

  closeAddNoteModal(): void {
    if (this.isSavingManualNote) {
      return;
    }

    this.showAddNoteModal = false;
    this.addNoteError = '';
    this.manualNoteDraft = this.getDefaultManualNoteDraft();
  }

  saveManualNote(): void {
    if (!this.hasStoredCrmActor()) {
      this.addNoteError = 'CRM login is required before saving notes.';
      return;
    }

    const companyId = this.getManualLogCompanyId();
    const entityId = this.getManualLogEntityId(this.manualNoteDraft.entityType);
    if (companyId <= 0) {
      this.addNoteError = 'This note cannot be saved because the CRM company id is missing.';
      return;
    }

    if (entityId <= 0) {
      this.addNoteError = 'The selected entity type does not have an available CRM target on this page.';
      return;
    }

    if (!this.canSaveManualNote) {
      this.addNoteError = 'Entity type and note body are required.';
      return;
    }

    this.addNoteError = '';
    this.isSavingManualNote = true;
    this.crmService.createClientNote(companyId, {
      entity_type: this.manualNoteDraft.entityType.trim(),
      entity_id: entityId,
      pinned: this.manualNoteDraft.pinned,
      body: this.manualNoteDraft.body.trim()
    }).subscribe(
      () => {
        this.isSavingManualNote = false;
        this.showAddNoteModal = false;
        this.manualNoteDraft = this.getDefaultManualNoteDraft();
        this.reloadCurrentDetails(true);
      },
      (error) => {
        this.isSavingManualNote = false;
        this.addNoteError = this.requestErrorMessage(error, 'Unable to add note.');
      }
    );
  }

  openEditNoteModal(note: CrmClientNote): void {
    this.editNoteDraft = {
      id: note.id,
      entityType: note.entityType,
      pinned: note.pinned,
      body: note.body || ''
    };
    this.editNoteError = '';
    this.showEditNoteModal = true;
  }

  closeEditNoteModal(): void {
    if (this.isSavingEditNote) {
      return;
    }

    this.showEditNoteModal = false;
    this.editNoteError = '';
    this.editNoteDraft = null;
  }

  saveEditNote(): void {
    if (!this.hasStoredCrmActor()) {
      this.editNoteError = 'CRM login is required before saving notes.';
      return;
    }

    const companyId = this.getManualLogCompanyId();
    if (companyId <= 0) {
      this.editNoteError = 'This note cannot be saved because the CRM company id is missing.';
      return;
    }

    if (!this.editNoteDraft || !this.canSaveEditNote) {
      this.editNoteError = 'Entity type and note body are required.';
      return;
    }

    this.editNoteError = '';
    this.isSavingEditNote = true;
    this.crmService.updateClientNote(companyId, this.editNoteDraft.id, {
      entity_type: this.editNoteDraft.entityType.trim(),
      pinned: this.editNoteDraft.pinned,
      body: this.editNoteDraft.body.trim()
    }).subscribe(
      () => {
        this.isSavingEditNote = false;
        this.showEditNoteModal = false;
        this.editNoteDraft = null;
        this.reloadCurrentDetails(true);
      },
      (error) => {
        this.isSavingEditNote = false;
        this.editNoteError = this.requestErrorMessage(error, 'Unable to update note.');
      }
    );
  }

  openAddActivityModal(): void {
    this.manualActivityDraft = this.getDefaultManualActivityDraft();
    this.addActivityError = '';
    this.showAddActivityModal = true;
  }

  closeAddActivityModal(): void {
    if (this.isSavingManualActivity) {
      return;
    }

    this.showAddActivityModal = false;
    this.addActivityError = '';
    this.manualActivityDraft = this.getDefaultManualActivityDraft();
  }

  saveManualActivity(): void {
    if (!this.hasStoredCrmActor()) {
      this.addActivityError = 'CRM login is required before saving activities.';
      return;
    }

    const companyId = this.getManualLogCompanyId();
    const entityId = this.getManualLogEntityId(this.manualActivityDraft.entityType);
    if (companyId <= 0) {
      this.addActivityError = 'This activity cannot be saved because the CRM company id is missing.';
      return;
    }

    if (entityId <= 0) {
      this.addActivityError = 'The selected entity type does not have an available CRM target on this page.';
      return;
    }

    if (!this.canSaveManualActivity) {
      this.addActivityError = 'Entity type, activity date, action, and details are required.';
      return;
    }

    this.addActivityError = '';
    this.isSavingManualActivity = true;
    this.crmService.createClientActivity(companyId, {
      entity_type: this.manualActivityDraft.entityType.trim(),
      entity_id: entityId,
      created: this.manualActivityDraft.created.trim(),
      action: this.manualActivityDraft.action.trim(),
      details: this.manualActivityDraft.details.trim()
    }).subscribe(
      () => {
        this.isSavingManualActivity = false;
        this.showAddActivityModal = false;
        this.manualActivityDraft = this.getDefaultManualActivityDraft();
        this.reloadCurrentDetails(true);
      },
      (error) => {
        this.isSavingManualActivity = false;
        this.addActivityError = this.requestErrorMessage(error, 'Unable to add activity.');
      }
    );
  }

  openAddActionModal(): void {
    this.editingActionId = null;
    this.manualActionDraft = this.getDefaultManualActionDraft();
    this.addActionError = '';
    this.showAddActionModal = true;
  }

  openEditActionModal(action: CrmActionItem): void {
    if (!action || !action.customAction || this.isClientActionClosed(action) || this.isActionModalBusy(action)) {
      return;
    }

    this.editingActionId = action.id;
    this.manualActionDraft = {
      level: action.level || 'Medium',
      action: action.kind,
      details: action.details || '',
      actionDate: this.getDateTimeInputValue(new Date(action.created)),
      dueDate: this.getDateTimeInputValue(new Date(action.actionDate))
    };
    this.addActionError = '';
    this.showAddActionModal = true;
  }

  closeAddActionModal(): void {
    if (this.isSavingClientAction) {
      return;
    }

    this.showAddActionModal = false;
    this.addActionError = '';
    this.editingActionId = null;
    this.manualActionDraft = this.getDefaultManualActionDraft();
  }

  saveClientAction(): void {
    if (!this.hasStoredCrmActor()) {
      this.addActionError = 'CRM login is required before saving actions.';
      return;
    }

    const companyId = this.getManualLogCompanyId();
    if (companyId <= 0) {
      this.addActionError = 'This action cannot be saved because the CRM company id is missing.';
      return;
    }

    if (!this.canSaveClientAction) {
      this.addActionError = 'Level, action, details, action date, and due date are required.';
      return;
    }

    this.addActionError = '';
    this.isSavingClientAction = true;
    const payload = {
      level: this.manualActionDraft.level,
      action: this.manualActionDraft.action.trim(),
      details: this.manualActionDraft.details.trim(),
      action_date: this.manualActionDraft.dueDate.trim(),
      created: this.manualActionDraft.actionDate.trim()
    };
    const request$ = this.editingActionId
      ? this.crmService.updateClientAction(companyId, this.editingActionId, payload)
      : this.crmService.createClientAction(companyId, payload);

    request$.subscribe(
      (response) => {
        this.applyClientActionMutationResponse(response.action || null, response.actions || []);
        this.isSavingClientAction = false;
        this.showAddActionModal = false;
        this.editingActionId = null;
        this.manualActionDraft = this.getDefaultManualActionDraft();
      },
      (error) => {
        this.isSavingClientAction = false;
        this.addActionError = this.requestErrorMessage(error, this.editingActionId ? 'Unable to update action.' : 'Unable to add action.');
      }
    );
  }

  completeClientAction(action: CrmActionItem): void {
    if (!action || !action.customAction || this.isClientActionClosed(action) || this.isActionModalBusy(action)) {
      return;
    }

    if (!this.hasStoredCrmActor()) {
      this.completeActionError = 'CRM login is required before completing actions.';
      return;
    }

    const companyId = action.companyId || this.getManualLogCompanyId();
    if (!companyId) {
      this.completeActionError = 'This action cannot be completed because the CRM company id is missing.';
      return;
    }

    this.addActionError = '';
    this.completeActionError = '';
    this.completingActionIds.add(action.id);
    this.crmService.completeClientAction(companyId, action.id).subscribe(
      (response) => {
        this.applyClientActionMutationResponse(response.action || null, response.actions || []);
        this.completingActionIds.delete(action.id);
        this.showCompleteActionConfirmModal = false;
        this.actionPendingCompletion = null;
      },
      (error) => {
        this.completingActionIds.delete(action.id);
        this.completeActionError = this.requestErrorMessage(error, 'Unable to complete action.');
      }
    );
  }

  onShowCompletedActionsChange(): void {
    this.refreshActionTableRows();
  }

  updateActionTableRows(rows: CrmActionItem[]): void {
    this.actionTableRows = rows || [];
  }

  isCompletingClientAction(action: CrmActionItem): boolean {
    return this.completingActionIds.has(action.id);
  }

  isDisregardingClientAction(action: CrmActionItem): boolean {
    return this.disregardingActionIds.has(action.id);
  }

  isActionModalBusy(action: CrmActionItem | null): boolean {
    return !!action && (this.isCompletingClientAction(action) || this.isDisregardingClientAction(action));
  }

  disregardClientAction(action: CrmActionItem): void {
    if (!action || !action.customAction || this.isClientActionClosed(action) || this.isActionModalBusy(action)) {
      return;
    }

    if (!window.confirm('Disregard this action item? It will be closed as disregarded and kept in action history.')) {
      return;
    }

    if (!this.hasStoredCrmActor()) {
      this.completeActionError = 'CRM login is required before disregarding actions.';
      return;
    }

    const companyId = action.companyId || this.getManualLogCompanyId();
    if (!companyId) {
      this.completeActionError = 'This action cannot be disregarded because the CRM company id is missing.';
      return;
    }

    this.addActionError = '';
    this.completeActionError = '';
    this.disregardingActionIds.add(action.id);
    this.crmService.disregardClientAction(companyId, action.id).subscribe(
      (response) => {
        this.applyClientActionMutationResponse(response.action || null, response.actions || []);
        this.disregardingActionIds.delete(action.id);
        this.showCompleteActionConfirmModal = false;
        this.actionPendingCompletion = null;
      },
      (error) => {
        this.disregardingActionIds.delete(action.id);
        this.completeActionError = this.requestErrorMessage(error, 'Unable to disregard action.');
      }
    );
  }

  openCompleteActionConfirmModal(action: CrmActionItem): void {
    if (!action || !action.customAction || this.isClientActionClosed(action) || this.isActionModalBusy(action)) {
      return;
    }

    this.actionPendingCompletion = action;
    this.completeActionError = '';
    this.showCompleteActionConfirmModal = true;
  }

  closeCompleteActionConfirmModal(): void {
    if (this.isActionModalBusy(this.actionPendingCompletion)) {
      return;
    }

    this.showCompleteActionConfirmModal = false;
    this.completeActionError = '';
    this.actionPendingCompletion = null;
  }

  confirmCompleteClientAction(): void {
    if (!this.actionPendingCompletion) {
      this.completeActionError = 'No action was selected.';
      return;
    }

    this.completeClientAction(this.actionPendingCompletion);
  }

  openAddContactModal(): void {
    this.additionalContactDraft = this.getDefaultAdditionalContactDraft();
    this.editingAdditionalContactId = null;
    this.addContactError = '';
    this.activeDetailTagDropdown = null;
    this.showAddContactModal = true;
  }

  openEditAdditionalContactModal(contact: CrmContactPerson): void {
    if (!this.canDeleteContactTableRow(contact) || this.isPrimaryContact(contact) || this.isBillingContact(contact)) {
      this.contactDeleteError = 'Primary and billing contacts are managed in their own sections.';
      return;
    }

    this.contactDeleteError = '';
    this.additionalContactDraft = this.buildAdditionalContactDraftFromContact(contact);
    this.editingAdditionalContactId = contact.id;
    this.addContactError = '';
    this.activeDetailTagDropdown = null;
    this.showAddContactModal = true;
  }

  closeAddContactModal(): void {
    if (this.isSavingAdditionalContact) {
      return;
    }

    this.showAddContactModal = false;
    this.addContactError = '';
    this.editingAdditionalContactId = null;
    this.activeDetailTagDropdown = null;
    this.additionalContactDraft = this.getDefaultAdditionalContactDraft();
  }

  saveAdditionalContact(): void {
    const companyId = Number(this.clientId) || 0;
    if (companyId <= 0) {
      this.addContactError = 'This contact cannot be saved because the CRM company id is missing.';
      return;
    }

    if (!this.canSaveAdditionalContact) {
      this.addContactError = 'Enter at least a first or last name.';
      return;
    }

    this.isSavingAdditionalContact = true;
    this.addContactError = '';

    const editingContactId = Number(this.editingAdditionalContactId || 0);
    if (editingContactId > 0) {
      this.crmService.updateClient(companyId, {
        contacts: [{
          id: editingContactId,
          ...this.buildAdditionalContactPayload()
        }]
      }).subscribe(
        () => {
          const sourceContact = this.contactPersons.find((contact) => contact.id === editingContactId) || null;
          const updatedContact = this.buildAdditionalContactPersonFromDraft(editingContactId, sourceContact);
          let hasUpdatedContact = false;
          this.contactPersons = this.contactPersons.map((contact) => {
            if (contact.id !== editingContactId) {
              return contact;
            }

            hasUpdatedContact = true;
            return updatedContact;
          });

          if (!hasUpdatedContact) {
            this.contactPersons = [...this.contactPersons, updatedContact];
          }

          this.refreshContactTableRows();
          this.showAddContactModal = false;
          this.editingAdditionalContactId = null;
          this.additionalContactDraft = this.getDefaultAdditionalContactDraft();
          this.isSavingAdditionalContact = false;
        },
        (error) => {
          this.addContactError = this.requestErrorMessage(error, 'Unable to update contact.');
          this.isSavingAdditionalContact = false;
        }
      );
      return;
    }

    this.crmService.createClientContact(companyId, this.buildAdditionalContactPayload()).subscribe(
      (response) => {
        this.applyClientContactCreateResponse(response.contact || null, response.contacts || []);
        this.showAddContactModal = false;
        this.editingAdditionalContactId = null;
        this.additionalContactDraft = this.getDefaultAdditionalContactDraft();
        this.isSavingAdditionalContact = false;
      },
      (error) => {
        this.addContactError = this.requestErrorMessage(error, 'Unable to add contact.');
        this.isSavingAdditionalContact = false;
      }
    );
  }

  deleteEditingAdditionalContact(): void {
    const contact = this.editingAdditionalContact;
    if (!contact) {
      this.addContactError = 'This contact cannot be deleted because it is no longer available.';
      return;
    }

    this.addContactError = '';
    this.deleteAdditionalContact(contact);
  }

  goToActivityPage(page: number): void {
    this.currentActivityPage = Math.min(Math.max(page, 1), this.totalActivityPages);
  }

  updateActivityItemsPerPage(itemsPerPage: number | string): void {
    const nextPageSize = Number(itemsPerPage);
    this.activityItemsPerPage = this.activityPageSizeOptions.includes(nextPageSize) ? nextPageSize : this.activityPageSizeOptions[0];
    this.goToActivityPage(1);
  }

  onEditDraftChange(): void {
    if (!this.editDraft) {
      this.hasPendingChanges = false;
      return;
    }

    this.saveDraftError = '';
    this.hasPendingChanges = JSON.stringify(this.editDraft) !== this.initialEditDraftSnapshot;
    this.scheduleContactDependentTableRefresh();
  }

  onReferralSourceCompanyChange(): void {
    if (!this.editDraft) {
      return;
    }

    const selected = this.referralCompanyOptions.find(
      (option) => String(option.id) === this.editDraft!.referralSourceCompanyId
    );
    this.editDraft.referralSourceCompanyName = selected ? selected.name : '';
    this.editDraft.referralSourceContactId = '';
    this.editDraft.referralSourceContactName = '';
    this.loadReferralContactOptions();
    this.onEditDraftChange();
  }

  onReferralSourceContactChange(): void {
    if (!this.editDraft) {
      return;
    }

    const selected = this.referralContactOptions.find(
      (option) => String(option.id) === this.editDraft!.referralSourceContactId
    );
    this.editDraft.referralSourceContactName = selected ? selected.name : '';
    this.onEditDraftChange();
  }

  getReferralContactOptionLabel(option: CrmClientContactOption): string {
    if (!option.role || option.role.length === 0) {
      return option.isPrimary ? `${option.name} (Primary)` : option.name;
    }

    const roleLabels = option.role.map((role) => this.getDisplayLabel(role));
    return `${option.name} (${roleLabels.join(', ')})`;
  }

  viewReferralSourceClient(): void {
    const sourceCompanyId = this.referral?.sourceCompanyId;
    if (!sourceCompanyId) {
      return;
    }

    this.router.navigate(['/crm/details/client', sourceCompanyId]);
  }

  saveDraft(): void {
    if (!this.editDraft || this.isSavingDraft) {
      return;
    }

    const companyId = Number(this.clientId) || 0;
    if (companyId <= 0) {
      this.saveDraftError = 'This record cannot be saved because the CRM company id is missing.';
      return;
    }

    if (!this.editDraft.clientName.trim()) {
      this.saveDraftError = 'Enter a record name.';
      return;
    }

    this.isSavingDraft = true;
    this.saveDraftError = '';

    this.crmService.updateClient(companyId, this.buildClientUpdatePayload(this.editDraft)).subscribe(
      (response) => {
        if (response && response.referral !== undefined) {
          this.referral = response.referral;
        }
        this.isSavingDraft = false;
        this.hasPendingChanges = false;
        this.resetEditingSections();
        this.loadClientDetails(this.isClientDetailsRoute ? this.clientId : this.networkId, this.isClientDetailsRoute);
      },
      (error) => {
        this.saveDraftError = this.requestErrorMessage(error, 'Unable to save the record details.');
        this.isSavingDraft = false;
      }
    );
  }

  deleteAdditionalContact(contact: CrmContactPerson): void {
    const companyId = Number(this.clientId) || 0;
    if (companyId <= 0) {
      this.contactDeleteError = 'This contact cannot be deleted because the CRM company id is missing.';
      return;
    }

    if (!this.canDeleteContactTableRow(contact) || this.isPrimaryContact(contact) || this.isBillingContact(contact)) {
      this.contactDeleteError = 'Primary and billing contacts cannot be deleted from this table.';
      return;
    }

    if (this.isDeletingContact(contact)) {
      return;
    }

    this.contactDeleteError = '';
    this.setContactDeleting(contact.id, true);

    this.crmService.updateClient(companyId, {
      contacts: [{
        id: contact.id,
        _delete: true
      }]
    }).subscribe(
      () => {
        this.contactPersons = this.contactPersons.filter((candidate) => candidate.id !== contact.id);
        this.setContactDeleting(contact.id, false);
        this.refreshContactTableRows();
        if (this.editingAdditionalContactId === contact.id) {
          this.showAddContactModal = false;
          this.editingAdditionalContactId = null;
          this.additionalContactDraft = this.getDefaultAdditionalContactDraft();
        }
      },
      (error) => {
        this.contactDeleteError = this.requestErrorMessage(error, 'Unable to delete contact.');
        if (this.editingAdditionalContactId === contact.id) {
          this.addContactError = this.contactDeleteError;
        }
        this.setContactDeleting(contact.id, false);
      }
    );
  }

  private buildClientUpdatePayload(draft: CrmClientEditDraft): Record<string, unknown> {
    const payload: Record<string, unknown> = {
      name: draft.clientName.trim(),
      legal_name: this.nullIfEmpty(draft.legalName),
      stage: draft.stage,
      type: draft.type || null,
      source: this.nullIfEmpty(draft.source),
      converted: this.nullIfEmpty(draft.converted),
      industry: this.nullIfEmpty(draft.industry),
      website: this.nullIfEmpty(draft.website),
      phone: this.nullIfEmpty(draft.phone),
      email_general: this.nullIfEmpty(draft.emailGeneral),
      tags_csv: this.nullIfEmpty(draft.tagsCsv),
      notes_summary: this.nullIfEmpty(draft.notesSummary)
    };

    const primaryContact = this.buildPrimaryContactUpdatePayload(draft);
    if (this.shouldSavePrimaryContactPayload(primaryContact, draft)) {
      payload.primary_contact = primaryContact;
    }

    const billing = this.buildBillingUpdatePayload(draft);
    if (this.shouldSaveBillingPayload(billing)) {
      payload.company_billing = billing;
    }

    const lcInfo = this.buildLcInfoUpdatePayload(draft);
    if (lcInfo) {
      payload.lc_info = lcInfo;
    }

    const referral = this.buildReferralUpdatePayload(draft);
    if (this.shouldSaveReferralPayload(referral)) {
      payload.referral_record = referral;
    }

    return payload;
  }

  private buildPrimaryContactUpdatePayload(draft: CrmClientEditDraft): Record<string, unknown> {
    return {
      id: this.parseOptionalNumber(draft.primaryContactId),
      first_name: this.nullIfEmpty(draft.primaryContactFirstName),
      last_name: this.nullIfEmpty(draft.primaryContactLastName),
      title: this.nullIfEmpty(draft.primaryContactTitle),
      email: this.nullIfEmpty(draft.primaryContactEmail),
      email_alt: this.nullIfEmpty(draft.primaryContactAltEmail),
      phone: this.nullIfEmpty(draft.primaryContactPhone),
      phone_alt: this.nullIfEmpty(draft.primaryContactAltPhone),
      phone_norm: this.normalizePhone(this.nullIfEmpty(draft.primaryContactPhone)),
      preferred_contact: this.normalizePreferredContact(draft.primaryContactPreferredContact),
      user_id: this.parseOptionalNumber(draft.primaryContactUserId),
      role: this.splitCsv(draft.primaryContactRolesCsv),
      is_primary_for_company: true,
      tags_csv: this.nullIfEmpty(draft.primaryContactTagsCsv),
      contact_note: this.nullIfEmpty(draft.primaryContactNotes)
    };
  }

  private buildBillingUpdatePayload(draft: CrmClientEditDraft): Record<string, unknown> {
    return {
      billing_contact_name: this.nullIfEmpty(draft.billingContactName),
      billing_email: this.nullIfEmpty(draft.billingEmail),
      billing_phone: this.nullIfEmpty(draft.billingPhone),
      primary_registrar_email: this.nullIfEmpty(draft.primaryRegistrarEmail),
      alternate_account_contact_json: this.buildAltenateAccountContact(
        draft.alternateAccountContactName,
        draft.alternateAccountContactEmail,
        draft.alternateAccountContactPhone
      ),
      billing_instructions: this.nullIfEmpty(draft.billingInstructions)
    };
  }

  private buildLcInfoUpdatePayload(draft: CrmClientEditDraft): Record<string, unknown> | null {
    const networkId = this.parseOptionalNumber(draft.lcNetworkId);
    if (!networkId) {
      return null;
    }

    return {
      network_id: networkId,
      network_name: this.nullIfEmpty(draft.lcName),
      login_page: this.nullIfEmpty(draft.lcLoginPage),
      subscription_start_date: this.nullIfEmpty(draft.lcSubscriptionStart),
      subscription_end_date: this.nullIfEmpty(draft.lcSubscriptionEnd),
      advanced_payment: draft.lcAdvancePay ? 1 : 0,
      reference: this.nullIfEmpty(draft.lcInvoiceRef),
      notes: this.nullIfEmpty(draft.lcNote)
    };
  }

  private buildReferralUpdatePayload(draft: CrmClientEditDraft): Record<string, unknown> {
    return {
      source_company_id: this.parseOptionalNumber(draft.referralSourceCompanyId),
      source_contact_id: this.parseOptionalNumber(draft.referralSourceContactId),
      program: this.nullIfEmpty(draft.referralProgram),
      referred: this.nullIfEmpty(draft.referred),
      notes: this.nullIfEmpty(draft.referralNotes)
    };
  }

  private shouldSavePrimaryContactPayload(payload: Record<string, unknown>, draft: CrmClientEditDraft): boolean {
    return this.parseOptionalNumber(draft.primaryContactId) !== null
      || this.hasAnyPayloadValue(payload, ['id', 'is_primary_for_company', 'preferred_contact']);
  }

  private shouldSaveBillingPayload(payload: Record<string, unknown>): boolean {
    return this.hasAnyPayloadValue(payload)
      || this.hasInitialDraftValues([
        'billingContactName',
        'billingEmail',
        'billingPhone',
        'primaryRegistrarEmail',
        'alternateAccountContactName',
        'alternateAccountContactEmail',
        'alternateAccountContactPhone',
        'billingInstructions'
      ]);
  }

  private shouldSaveReferralPayload(payload: Record<string, unknown>): boolean {
    return this.hasAnyPayloadValue(payload)
      || this.hasInitialDraftValues([
        'referralSourceCompanyId',
        'referralSourceContactId',
        'referralProgram',
        'referred',
        'referralNotes'
      ]);
  }

  private hasAnyPayloadValue(payload: Record<string, unknown>, ignoredKeys: string[] = []): boolean {
    const ignored = new Set(ignoredKeys);
    return Object.keys(payload).some((key) => {
      if (ignored.has(key)) {
        return false;
      }

      const value = payload[key];
      if (Array.isArray(value)) {
        return value.length > 0;
      }

      if (value && typeof value === 'object') {
        return Object.values(value).some((nestedValue) => nestedValue !== null && nestedValue !== '');
      }

      return value !== null && value !== '';
    });
  }

  private hasInitialDraftValues(keys: Array<keyof CrmClientEditDraft>): boolean {
    if (!this.initialEditDraftSnapshot) {
      return false;
    }

    const initialDraft = JSON.parse(this.initialEditDraftSnapshot) as CrmClientEditDraft;
    return keys.some((key) => {
      const value = initialDraft[key];
      return typeof value === 'string' ? value.trim().length > 0 : !!value;
    });
  }

  cancelDraftChanges(): void {
    if (!this.initialEditDraftSnapshot) {
      return;
    }

    this.editDraft = JSON.parse(this.initialEditDraftSnapshot) as CrmClientEditDraft;
    this.hasPendingChanges = false;
    this.resetEditingSections();
  }

  getDisplayLabel(value: string | null | undefined): string {
    if (!value) {
      return '-';
    }

    if (value === 'lc') {
      return 'LC';
    }

    return value
      .replace(/[-_]+/g, ' ')
      .replace(/\blc\b/gi, 'LC')
      .replace(/\b\w/g, (character) => character.toUpperCase());
  }

  getActionLabel(action: CrmActionItem): string {
    if (action.customAction && action.kind === 'other') {
      return 'Other';
    }

    return action.summary || this.getDisplayLabel(action.kind);
  }

  getActionDetails(action: CrmActionItem): string {
    return action.details || action.otherText || action.summary || '-';
  }

  getActionCreatedBy(action: CrmActionItem): string {
    return action.createdBy || action.assignedTo || '-';
  }

  getActionLevelClass(level: CrmActionLevel | undefined): string {
    switch (level) {
      case 'Low':
        return 'action-level-low';
      case 'High':
        return 'action-level-high';
      case 'Critical':
        return 'action-level-critical';
      case 'Medium':
      default:
        return 'action-level-medium';
    }
  }

  getActionStatusClass(status: string | null | undefined): string {
    if (status === 'complete') {
      return 'status-complete';
    }

    if (status === 'disregarded') {
      return 'status-disregarded';
    }

    return 'status-open';
  }

  isClientActionClosed(action: CrmActionItem | null | undefined): boolean {
    return !!action && action.status !== 'open';
  }

  getCrmContactFullName(contact: CrmContact): string {
    const firstName = (contact.first_name || '').trim();
    const lastName = (contact.last_name || '').trim();
    const fullName = `${firstName} ${lastName}`.trim();

    return fullName || '-';
  }

  private isPrimaryContact(contact: CrmContact): boolean {
    if (contact.is_primary_for_company) {
      return true;
    }

    const primaryIdValue = (this.editDraft && this.editDraft.primaryContactId)
      || (this.primaryContact ? String(this.primaryContact.id) : null);
    const primaryId = this.parseOptionalNumber(primaryIdValue);
    if (primaryId !== null && contact.id === primaryId) {
      return true;
    }

    const contactName = this.normalizeContactMatchValue(this.getCrmContactFullName(contact));
    const primaryName = this.normalizeContactMatchValue(
      this.editDraft
        ? `${this.editDraft.primaryContactFirstName} ${this.editDraft.primaryContactLastName}`
        : (this.primaryContact ? this.getCrmContactFullName(this.primaryContact) : '')
    );
    const primaryEmail = this.normalizeContactMatchValue(
      (this.editDraft && this.editDraft.primaryContactEmail) || (this.primaryContact && this.primaryContact.email) || ''
    );
    const primaryPhone = this.normalizePhoneForComparison(
      (this.editDraft && this.editDraft.primaryContactPhone) || (this.primaryContact && this.primaryContact.phone) || ''
    );
    const contactEmails = [
      contact.email,
      contact.email_alt
    ].map((value) => this.normalizeContactMatchValue(value));
    const contactPhones = [
      contact.phone,
      contact.phone_office,
      contact.phone_cell,
      contact.phone_alt,
      contact.phone_norm
    ].map((value) => this.normalizePhoneForComparison(value));

    return (!!primaryName && contactName === primaryName)
      || (!!primaryEmail && contactEmails.includes(primaryEmail))
      || (!!primaryPhone && contactPhones.includes(primaryPhone));
  }

  private isBillingContact(contact: CrmContact): boolean {
    const contactName = this.normalizeContactMatchValue(this.getCrmContactFullName(contact));
    const billingName = this.normalizeContactMatchValue(
      (this.editDraft && this.editDraft.billingContactName) || (this.billing && this.billing.billingContactName) || ''
    );
    const billingEmail = this.normalizeContactMatchValue(
      (this.editDraft && this.editDraft.billingEmail) || (this.billing && this.billing.billingEmail) || ''
    );
    const billingPhone = this.normalizePhoneForComparison(
      (this.editDraft && this.editDraft.billingPhone) || (this.billing && this.billing.billingPhone) || ''
    );
    const contactEmails = [
      contact.email,
      contact.email_alt
    ].map((value) => this.normalizeContactMatchValue(value));
    const contactPhones = [
      contact.phone,
      contact.phone_office,
      contact.phone_cell,
      contact.phone_alt,
      contact.phone_norm
    ].map((value) => this.normalizePhoneForComparison(value));
    const metadata = [
      contact.title,
      contact.position,
      contact.department,
      contact.tags_csv,
      contact.role ? contact.role.join(' ') : '',
      this.contactValueAsString(contact['role_hint'])
    ].map((value) => this.normalizeContactMatchValue(value)).join(' ');

    return (!!billingName && contactName === billingName)
      || contactName === 'billing contact'
      || (!!billingEmail && contactEmails.includes(billingEmail))
      || (!!billingPhone && contactPhones.includes(billingPhone))
      || metadata.indexOf('billing') >= 0;
  }

  private normalizeContactMatchValue(value: unknown): string {
    return typeof value === 'string' ? value.trim().replace(/\s+/g, ' ').toLowerCase() : '';
  }

  private normalizePhoneForComparison(value: unknown): string {
    return typeof value === 'string' ? value.replace(/\D+/g, '') : '';
  }

  private contactValueAsString(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  getLcPrimaryContactEntries(contact: CrmLcPrimaryContact | null): Array<{ label: string; value: string }> {
    if (!contact) {
      return [];
    }

    return Object.keys(contact).map((key) => ({
      label: this.formatContactFieldLabel(key),
      value: this.formatContactFieldValue(contact[key as keyof CrmLcPrimaryContact])
    }));
  }

  getEntityTypeLabel(entityType: CrmActivityEntityType): string {
    switch (entityType) {
      case 'company':
        return 'Client';
      case 'lc':
        return 'LC';
      case 'contact':
        return 'Contact';
      case 'system':
        return 'System';
      case 'user':
        return 'User';
      case 'sync':
        return 'Sync';
      default:
        return this.getDisplayLabel(entityType);
    }
  }

  getActivityActorLabel(activity: CrmClientActivity): string {
    return activity.entityType === 'system' || activity.entityType === 'sync' ? 'Triggered By' : 'Created By';
  }

  getEntityTypeClass(entityType: CrmActivityEntityType): string {
    switch (entityType) {
      case 'company':
        return 'entity-company';
      case 'lc':
        return 'entity-lc';
      case 'contact':
        return 'entity-contact';
      case 'system':
        return 'entity-system';
      case 'user':
        return 'entity-user';
      default:
        return 'entity-company';
    }
  }

  getNetworkStatusClass(status: CrmClientNetwork['crm_status'] | string): string {
    switch (status) {
      case 'trial':
        return 'status-trial';
      case 'workplace':
        return 'status-workplace';
      case 'trainers':
        return 'status-trainers';
      case 'hybrid':
        return 'status-hybrid';
      case 'vendor':
        return 'status-vendor';
      case 'no-lc':
        return 'status-inactive';
      default:
        return 'status-workplace';
    }
  }

  getClientStageClass(stage: CrmCompanyStage): string {
    switch (stage) {
      case 'client':
        return 'status-active';
      case 'proposal':
        return 'status-proposal';
      case 'trial':
        return 'status-hybrid';
      case 'at_risk':
        return 'status-at-risk';
      case 'paused':
        return 'status-paused';
      case 'lost':
        return 'status-lost';
      case 'prospect':
      default:
        return 'status-demo';
    }
  }

  isSavingHeaderField(field: 'stage' | 'type'): boolean {
    return this.savingHeaderFields.has(field);
  }

  onHeaderStageChange(stage: CrmCompanyStage): void {
    if (!this.summary || this.summary.stage === stage) {
      return;
    }

    const companyId = this.getManualLogCompanyId();
    if (companyId <= 0) {
      this.headerFieldError = 'This record cannot be saved because it is missing a CRM company id.';
      return;
    }

    const previousStage = this.summary.stage;
    this.headerFieldError = '';
    this.savingHeaderFields.add('stage');
    this.summary = { ...this.summary, stage };

    this.crmService.updateClient(companyId, { stage }).subscribe(
      () => {
        this.savingHeaderFields.delete('stage');
      },
      (error) => {
        if (this.summary) {
          this.summary = { ...this.summary, stage: previousStage };
        }
        this.headerFieldError = this.requestErrorMessage(error, 'Unable to save the stage.');
        this.savingHeaderFields.delete('stage');
      }
    );
  }

  onHeaderTypeChange(type: CrmCompanyType): void {
    if (!this.summary || this.summary.type === type) {
      return;
    }

    const companyId = this.getManualLogCompanyId();
    if (companyId <= 0) {
      this.headerFieldError = 'This record cannot be saved because it is missing a CRM company id.';
      return;
    }

    const previousType = this.summary.type;
    this.headerFieldError = '';
    this.savingHeaderFields.add('type');
    this.summary = { ...this.summary, type };

    this.crmService.updateClient(companyId, { type }).subscribe(
      () => {
        this.savingHeaderFields.delete('type');
      },
      (error) => {
        if (this.summary) {
          this.summary = { ...this.summary, type: previousType };
        }
        this.headerFieldError = this.requestErrorMessage(error, 'Unable to save the type.');
        this.savingHeaderFields.delete('type');
      }
    );
  }

  getClientTypeClass(type: CrmCompanyType | '' | null | undefined): string {
    switch (type) {
      case 'customer':
        return 'type-client';
      case 'partner':
      case 'internal':
        return 'type-opportunity';
      case 'lead':
        return 'type-lead';
      default:
        return 'type-prospect';
    }
  }

  getUserStatusClass(status: CrmClientUser['status']): string {
    switch (status) {
      case 'active':
        return 'status-active';
      case 'invited':
        return 'status-invited';
      case 'inactive':
        return 'status-inactive';
      default:
        return 'status-active';
    }
  }

  getScopeClass(scope: CrmClientChangeLogEntry['scope']): string {
    return scope === 'client' ? 'scope-client' : 'scope-user';
  }

  getBillingSyncClass(status: CrmBillingSyncStatus): string {
    switch (status) {
      case 'healthy':
        return 'sync-healthy';
      case 'warning':
        return 'sync-warning';
      case 'missing':
        return 'sync-missing';
      default:
        return 'sync-warning';
    }
  }

  trackByNetworkRowId(index: number, row: CrmClientNetworkRow): number {
    return row.network.id;
  }

  trackByUserRowId(index: number, row: CrmClientUserRow): number {
    return row.user.id;
  }

  trackByChangeRowId(index: number, row: CrmClientChangeLogRow): number {
    return row.entry.id;
  }

  trackByNoteId(index: number, note: CrmClientNote): number {
    return note.id;
  }

  trackByActivityId(index: number, activity: CrmClientActivity): number {
    return activity.id;
  }

  trackByActivityChange(index: number, change: CrmActivityFieldChange): string {
    return `${change.field}-${index}`;
  }

  trackByContactEntry(index: number, field: { label: string; value: string }): string {
    return `${field.label}-${index}`;
  }

  trackByPageSizeOption(index: number, option: number): number {
    return option;
  }

  trackByPrimaryNetworkId(index: number, option: CrmPrimaryNetworkOption): number {
    return option.network_id;
  }

  trackByBreakdownKey(index: number, item: CrmBreakdownItem): string {
    return item.key;
  }

  trackByTokenRoleId(index: number, item: { roleId: number }): number {
    return item.roleId;
  }

  trackByContactPersonId(index: number, contact: CrmContactPerson): string {
    const tableRowId = contact['tableRowId'];
    return typeof tableRowId === 'string' && tableRowId.trim()
      ? tableRowId
      : String(contact.id);
  }

  trackByManagedVendorLcId(index: number, row: CrmManagedVendorLcRow): number {
    return row.lc.networkId;
  }

  trackByActionId(index: number, action: CrmActionItem): string {
    return `${action.customAction ? 'custom' : 'system'}-${action.id}-${index}`;
  }

  trackByThreadId(index: number, thread: CrmCommunicationThread): number {
    return thread.id;
  }

  trackBySupportTicketId(index: number, ticket: CrmSupportTicketPlaceholder): number {
    return ticket.id;
  }

  trackByInvoiceId(index: number, invoice: CrmQuickBooksInvoicePlaceholder): number {
    return invoice.id;
  }

  trackByDuplicateCandidateId(index: number, candidate: CrmDuplicateCandidate): number {
    return candidate.id;
  }

  trackByText(index: number, value: string): string {
    return value;
  }

  getActivityChanges(activity: CrmClientActivity): CrmActivityFieldChange[] {
    if (this.isDeletedContactActivity(activity)) {
      return [];
    }

    const before = activity.before_json || {};
    const after = activity.after_json || {};
    const keys = Array.from(new Set([
      ...Object.keys(before),
      ...Object.keys(after)
    ]));

    return keys
      .map((key) => {
        const oldValue = this.formatActivityChangeValue(before[key]);
        const newValue = this.formatActivityChangeValue(after[key]);
        return {
          field: this.formatActivityFieldLabel(key),
          oldValue,
          newValue,
          hasPreviousValue: oldValue !== '-'
        };
      })
      .filter((change) => change.oldValue !== change.newValue);
  }

  getActivityDetails(activity: CrmClientActivity): string {
    const deletedContactDetails = this.getDeletedContactActivityDetails(activity);
    if (deletedContactDetails) {
      return deletedContactDetails;
    }

    const changes = this.getActivityChanges(activity);
    if (changes.length > 0) {
      const changeDetails = changes.map((change) => this.formatActivityChangeLine(change)).join('; ');
      const enteredDetails = this.getActivityMetaText(activity, 'details') || this.getActivityMetaText(activity, 'summary');
      return enteredDetails ? `${enteredDetails}; ${changeDetails}` : changeDetails;
    }

    const enteredDetails = this.getActivityMetaText(activity, 'details') || this.getActivityMetaText(activity, 'summary');
    if (enteredDetails) {
      return enteredDetails;
    }

    return '-';
  }

  getActivityIntro(activity: CrmClientActivity, activityChanges: CrmActivityFieldChange[] = this.getActivityChanges(activity)): string {
    if (this.isDeletedContactActivity(activity) || activityChanges.length === 0) {
      return '';
    }

    return this.getActivityMetaText(activity, 'details') || this.getActivityMetaText(activity, 'summary');
  }

  private isDeletedContactActivity(activity: CrmClientActivity): boolean {
    const parsedDetails = this.parseDeletedContactActivityText(activity);
    if (parsedDetails) {
      return true;
    }

    return activity.action === 'contact'
      && (
        this.getActivityMetaText(activity, 'details').toLowerCase() === 'contact deleted.'
        || this.getActivityMetaValue(activity, 'deleted_contact_name').length > 0
        || this.getActivityMetaValue(activity, 'deleted_user_id').length > 0
        || this.getActivityMetaValue(activity, 'deleted_preferred_contact').length > 0
      );
  }

  getDeletedContactActivityParts(activity: CrmClientActivity): CrmDeletedContactActivityDetails | null {
    const parsedDetails = this.parseDeletedContactActivityText(activity);
    if (parsedDetails) {
      return parsedDetails;
    }

    if (
      activity.action !== 'contact'
      || (
        this.getActivityMetaText(activity, 'details').toLowerCase() !== 'contact deleted.'
        && this.getActivityMetaValue(activity, 'deleted_contact_name').length === 0
        && this.getActivityMetaValue(activity, 'deleted_user_id').length === 0
        && this.getActivityMetaValue(activity, 'deleted_preferred_contact').length === 0
      )
    ) {
      return null;
    }

    const before = activity.before_json || {};
    const userId = this.getActivityMetaValue(activity, 'deleted_user_id')
      || this.getActivityMetaValue(activity, 'user_id')
      || this.getActivityMetaValue(activity, 'contact_id')
      || this.getObjectDisplayValue(before, 'user_id')
      || this.getObjectDisplayValue(before, 'contact_id');
    const name = this.getActivityMetaValue(activity, 'deleted_contact_name')
      || this.getActivityMetaValue(activity, 'contact_name')
      || this.getObjectDisplayValue(before, 'contact');
    const preferredContact = this.getActivityMetaValue(activity, 'deleted_preferred_contact')
      || this.getActivityMetaValue(activity, 'deleted_preferred_contact_method')
      || this.getActivityMetaValue(activity, 'preferred_contact')
      || this.getObjectDisplayValue(before, 'preferred_contact');

    return {
      title: `Contact ${name || '-'} Deleted`,
      userId: userId || '-',
      preferredContact: preferredContact || '-'
    };
  }

  private getDeletedContactActivityDetails(activity: CrmClientActivity): string {
    const details = this.getDeletedContactActivityParts(activity);
    if (!details) {
      return '';
    }

    return [
      details.title,
      `Userid: ${details.userId}`,
      `Preferred contact: ${details.preferredContact}`
    ].join('\n');
  }

  private parseDeletedContactActivityText(activity: CrmClientActivity): CrmDeletedContactActivityDetails | null {
    const details = this.getActivityMetaText(activity, 'details')
      || this.getActivityMetaText(activity, 'summary')
      || (typeof activity.summary === 'string' ? activity.summary.trim() : '');
    if (!details) {
      return null;
    }

    const lines = details
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
    const title = lines.find((line) => /^Contact\s+.+\s+Deleted$/i.test(line));
    if (!title) {
      return null;
    }

    return {
      title,
      userId: this.valueAfterActivityLabel(lines, /^User\s*id\s*:/i) || this.valueAfterActivityLabel(lines, /^Userid\s*:/i) || '-',
      preferredContact: this.valueAfterActivityLabel(lines, /^Preferred contact\s*:/i) || '-'
    };
  }

  private valueAfterActivityLabel(lines: string[], labelPattern: RegExp): string {
    const line = lines.find((item) => labelPattern.test(item));
    return line ? line.replace(labelPattern, '').trim() : '';
  }

  private getObjectDisplayValue(source: Record<string, unknown>, key: string): string {
    if (!source || source[key] === null || source[key] === undefined || source[key] === '') {
      return '';
    }

    return this.formatActivityChangeValue(source[key]);
  }

  private getActivityMetaText(activity: CrmClientActivity, key: string): string {
    if (!activity.meta_json || typeof activity.meta_json !== 'object') {
      return '';
    }

    const value = activity.meta_json[key];
    return typeof value === 'string' ? value.trim() : '';
  }

  private getActivityMetaValue(activity: CrmClientActivity, key: string): string {
    if (!activity.meta_json || typeof activity.meta_json !== 'object') {
      return '';
    }

    const value = activity.meta_json[key];
    if (value === null || value === undefined || value === '') {
      return '';
    }

    return this.formatActivityChangeValue(value).trim();
  }

  isSectionExpanded(section: CrmDetailsSection): boolean {
    return this.expandedSections[section];
  }

  toggleSection(section: CrmDetailsSection): void {
    const nextExpanded = !this.expandedSections[section];
    this.expandedSections[section] = nextExpanded;

    if (!this.isDetailedDetailsView && (section === 'primaryContact' || section === 'billing')) {
      const pairedSection: CrmDetailsSection = section === 'primaryContact' ? 'billing' : 'primaryContact';
      this.expandedSections[pairedSection] = nextExpanded;
    }
  }

  private syncSimpleContactBillingExpansion(): void {
    const shouldExpandBoth = this.expandedSections.primaryContact || this.expandedSections.billing;
    this.expandedSections.primaryContact = shouldExpandBoth;
    this.expandedSections.billing = shouldExpandBoth;
  }

  isSectionEditing(section: CrmEditableDetailsSection): boolean {
    return this.editingSections[section];
  }

  toggleSectionEdit(section: CrmEditableDetailsSection): void {
    const nextEditingState = !this.editingSections[section];
    this.editingSections = {
      ...this.editingSections,
      [section]: nextEditingState
    };

    if (nextEditingState && section !== 'companyProfile' && section !== 'lcInfo') {
      this.expandedSections[section] = true;
    }
  }


  onPrintMenuSelect(optionId: string): void {
    switch (optionId) {
      case 'details-page-report':
        this.printDetailsPageReport();
        break;
      case 'users-report':
        this.printUsersReport();
        break;
      case 'activity-report':
        this.printActivityReport();
        break;
      default:
        break;
    }
  }

  private printDetailsPageReport(): void {
    if (!this.editDraft) {
      return;
    }

    const sections = [
      renderPrintSection(
        'Report Snapshot',
        renderFieldGrid([
          { label: 'Record ID', value: this.clientId },
          { label: 'Record Name', value: this.displayClientName },
          { label: 'Vendor LC', value: this.vendorLcLabel },
          { label: 'Vendor Primary Contact', value: this.vendorLcPrimaryContactLabel },
          { label: 'User Count', value: this.users.length },
          { label: 'Active Users', value: this.activeUsersCount },
          { label: 'Used Tokens', value: this.inUseTokensTotal },
          { label: 'Available Tokens', value: this.unusedTokensTotal },
          ...this.leaningDataRows.map((item) => ({ label: item.label, value: item.count }))
        ]),
        'Full-page CRM record snapshot for the active detail view.'
      ),
      renderPrintSection(
        'Metric Cards',
        renderMetricGrid(this.buildDetailsMetricCards()),
        'All metric-card components from the page header.'
      ),
      renderPrintSection(
        'Record Overview',
        renderFieldGrid(this.buildClientOverviewFields()),
        'Core record-level CRM data and primary contact context.'
      ),
      renderPrintSection(
        'Primary Contact',
        renderFieldGrid(this.buildPrimaryContactFields()),
        'Primary contact record for this CRM record.'
      ),
      renderPrintSection(
        'Billing',
        renderFieldGrid(this.buildBillingFields()),
        'Billing primary contact, sync context, and registrar routing.'
      ),
      renderPrintSection(
        'Referral Source',
        renderFieldGrid(this.buildReferralFields()),
        'Referral and attribution details for this record.'
      ),
      renderPrintSection(
        'Notes',
        renderFieldCards(this.buildNoteCards(), 'No record notes are available.'),
        'Record notes captured for this CRM record.'
      )
    ];

    printCrmReport({
      title: 'CRM Details Page Report',
      subtitle: `Printable CRM record detail report for ${this.displayClientName}.`,
      meta: [`Record #${this.clientId}`, `${this.attachedLcCount} LCs`, `${this.users.length} users`],
      orientation: 'landscape',
      sections
    });
  }

  private printUsersReport(): void {
    const sections = [
      renderPrintSection(
        'LC Users List',
        renderTable(
          [
            { label: 'User', width: '18%' },
            { label: 'Email', width: '20%' },
            { label: 'Role', width: '16%' },
            { label: 'Groups', width: '12%' },
            { label: 'Status', width: '10%' },
            { label: 'Phone', width: '12%' },
            { label: 'Last Activity', width: '12%', nowrap: true }
          ],
          this.buildUserTableRows(),
          'No users are associated with this LC.'
        ),
        'Printable user list for the current CRM record.'
      )
    ];

    printCrmReport({
      title: 'CRM Users Report',
      subtitle: `Printable LC user list for ${this.displayClientName}.`,
      meta: [`Record #${this.clientId}`, `${this.users.length} users`],
      orientation: 'landscape',
      sections
    });
  }

  private printActivityReport(): void {
    const sections = [
      renderPrintSection(
        'Activity Items',
        renderFieldCards(this.buildActivityCards(), 'No CRM activities have been recorded for this record yet.'),
        'Activity items from the CRM record timeline.',
        true
      )
    ];

    printCrmReport({
      title: 'CRM Activity Report',
      subtitle: `Printable CRM activity report for ${this.displayClientName}.`,
      meta: [`Record #${this.clientId}`, `${this.activities.length} activities`],
      orientation: 'portrait',
      sections
    });
  }

  private buildDetailsMetricCards() {
    return [
      { label: 'Vendor LC', value: this.vendorLcLabel },
      { label: 'Total Users', value: this.users.length },
      { label: 'Active Users', value: this.activeUsersCount },
      { label: 'In-use Tokens', value: this.inUseTokensTotal },
      { label: 'Un-used Tokens', value: this.unusedTokensTotal }
    ];
  }

  private buildClientOverviewFields() {
    const draft = this.editDraft;

    if (!draft) {
      return [];
    }

    return [
      { label: 'Record Name', value: draft.clientName },
      { label: 'Legal Name', value: draft.legalName },
      { label: 'Stage', value: this.getDisplayLabel(draft.stage) },
      { label: 'Type', value: this.getDisplayLabel(draft.type) },
      { label: 'Source', value: this.getDisplayLabel(draft.source) },
      { label: 'Converted', value: draft.converted ? formatPrintDate(draft.converted, true) : '-' },
      { label: 'Industry', value: draft.industry },
      { label: 'Website', value: draft.website },
      { label: 'Phone', value: draft.phone },
      { label: 'General Email', value: draft.emailGeneral },
      { label: 'Address', value: draft.addressText, fullWidth: true },
      { label: 'Tags', value: this.draftTagList.length > 0 ? this.draftTagList.join(', ') : '-', fullWidth: true },
      { label: 'Summary Note', value: draft.notesSummary, fullWidth: true }
    ];
  }

  private buildBillingFields() {
    const draft = this.editDraft;

    if (!draft) {
      return [];
    }

    return [
      { label: 'Billing Contact', value: draft.billingContactName },
      { label: 'Billing Email', value: draft.billingEmail },
      { label: 'Billing Phone', value: draft.billingPhone },
      { label: 'Registrar Email', value: draft.primaryRegistrarEmail },
      { label: 'Last Pushed', value: formatPrintDate(this.billing ? this.billing.lastPushed : null, true) },
      { label: 'Last Pulled', value: formatPrintDate(this.billing ? this.billing.lastPulled : null, true) },
      { label: 'Sync Status', value: this.billingContactStatusLabel },
      {
        label: 'Altenate Account Contact',
        value: [
          draft.alternateAccountContactName || '-',
          draft.alternateAccountContactEmail || '-',
          draft.alternateAccountContactPhone || '-'
        ],
        fullWidth: true
      },
      { label: 'Billing Instructions', value: draft.billingInstructions, fullWidth: true }
    ];
  }

  private buildPrimaryContactFields() {
    const draft = this.editDraft;

    if (!draft) {
      return [];
    }

    return [
      { label: 'Contact ID', value: draft.primaryContactId || '-' },
      { label: 'Vendor ID', value: draft.primaryContactVendorId || '-' },
      { label: 'Name', value: `${draft.primaryContactFirstName} ${draft.primaryContactLastName}`.trim() || '-' },
      { label: 'Title', value: draft.primaryContactTitle || '-' },
      { label: 'Email', value: draft.primaryContactEmail || '-' },
      { label: 'Altenate Email', value: draft.primaryContactAltEmail || '-' },
      { label: 'Phone', value: draft.primaryContactPhone || '-' },
      { label: 'Altenate Phone', value: draft.primaryContactAltPhone || '-' },
      { label: 'Normalized Phone', value: draft.primaryContactPhoneNorm || '-' },
      { label: 'Preferred Contact', value: draft.primaryContactPreferredContact || '-' },
      { label: 'Company ID', value: draft.primaryContactCompanyId || '-' },
      { label: 'User ID', value: draft.primaryContactUserId || '-' },
      { label: 'Primary For Company', value: draft.primaryContactIsPrimary ? 'Yes' : 'No' },
      { label: 'Roles', value: draft.primaryContactRolesCsv || '-', fullWidth: true },
      { label: 'Tags', value: draft.primaryContactTagsCsv || '-', fullWidth: true },
      { label: 'Created', value: formatPrintDate(draft.primaryContactCreated, true) },
      { label: 'Modified', value: formatPrintDate(draft.primaryContactModified, true) },
      { label: 'Deleted', value: draft.primaryContactDeleted ? formatPrintDate(draft.primaryContactDeleted, true) : '-' }
    ];
  }

  private buildReferralFields() {
    const draft = this.editDraft;

    if (!draft) {
      return [];
    }

    return [
      { label: 'Referring Client', value: draft.referralSourceCompanyName },
      { label: 'Source Contact', value: draft.referralSourceContactName },
      { label: 'Program', value: draft.referralProgram },
      { label: 'Referred On', value: draft.referred ? formatPrintDate(draft.referred, true) : '-' },
      { label: 'Notes', value: draft.referralNotes, fullWidth: true }
    ];
  }

  private buildUserTableRows(): unknown[][] {
    return this.users.map((user) => [
      [user.name, `User #${user.id}`],
      user.email,
      this.getUserRolePrintLabel(user),
      this.getUserGroupsLabel(user),
      this.getDisplayLabel(user.status),
      user.phone || '-',
      formatPrintDate(user.lastActivity, true)
    ]);
  }

  private buildNoteCards() {
    return this.notes.map((note) => ({
      title: note.author,
      subtitle: this.getEntityTypeLabel(note.entityType),
      meta: [note.pinned ? 'Pinned' : 'Standard', formatPrintDate(note.created, true)],
      fields: [
        { label: 'Entity', value: this.getEntityTypeLabel(note.entityType) },
        { label: 'Created', value: formatPrintDate(note.created, true) },
        { label: 'Updated', value: formatPrintDate(note.modified, true) },
        { label: 'Note', value: note.body, fullWidth: true }
      ]
    }));
  }

  private buildActivityCards() {
    return this.activities.map((activity) => {
      return {
        title: this.getDisplayLabel(activity.action),
        subtitle: this.getActivityDetails(activity),
        meta: [this.getEntityTypeLabel(activity.entityType), formatPrintDate(activity.created, true)],
        fields: [
          { label: 'Entity Type', value: this.getEntityTypeLabel(activity.entityType) },
          { label: 'Action', value: this.getDisplayLabel(activity.action) },
          { label: 'Created', value: formatPrintDate(activity.created, true) },
          {
            label: 'Details',
            value: this.getActivityDetails(activity),
            fullWidth: true
          }
        ]
      };
    });
  }

  private formatActivityChangeValue(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    if (Array.isArray(value) || typeof value === 'object') {
      return JSON.stringify(value);
    }

    return String(value);
  }

  private formatActivityFieldLabel(value: string): string {
    return value
      .replace(/[_-]+/g, ' ')
      .replace(/\bcrm\b/gi, 'CRM')
      .replace(/\blc\b/gi, 'LC')
      .replace(/\b\w/g, (character) => character.toUpperCase());
  }

  private formatActivityChangeLine(change: CrmActivityFieldChange): string {
    if (!change.hasPreviousValue) {
      return `${change.field}: ${change.newValue}`;
    }

    return `${change.field}: ${change.oldValue} -> ${change.newValue}`;
  }

  private loadLiveAddLcOptions(): void {
    const requestId = ++this.addLcOptionsRequestId;
    this.hasLoadedLiveAddLcOptions = false;
    this.isLoadingAddLcOptions = true;
    this.addLcOptionsError = '';

    this.crmService.getAvailablePrimaryNetworks().subscribe(
      (options) => {
        if (requestId !== this.addLcOptionsRequestId) {
          return;
        }

        this.applyLiveAddLcOptions(options);
      },
      () => {
        if (requestId === this.addLcOptionsRequestId) {
          this.handleAddLcOptionsError('Error loading mock LCs.');
        }
      }
    );
  }

  private applyLiveAddLcOptions(options: CrmPrimaryNetworkOption[]): void {
    this.liveAddLcNetworks = Array.from(new Map(options.map((option) => [option.network_id, option])).values())
      .sort((left, right) => (left.network_name || '').localeCompare(right.network_name || ''));
    this.hasLoadedLiveAddLcOptions = true;
    this.refreshAddLcOptions();
    this.addLcOptionsPage = 1;
    this.isLoadingAddLcOptions = false;
  }

  private prepareAddLcOptionsForModal(): void {
    this.refreshAddLcOptions();

    if (!this.hasLoadedLiveAddLcOptions && !this.isLoadingAddLcOptions && !this.addLcOptionsError) {
      this.loadLiveAddLcOptions();
    }
  }

  private refreshAddLcOptions(): void {
    const editingNetworkIds = new Set(this.getEditingClientNetworks()
      .filter((network) => this.hasAttachedLc(network))
      .map((network) => network.network_id));
    const linkedNetworkIds = new Set(this.networks
      .filter((network) => this.hasAttachedLc(network) && !editingNetworkIds.has(network.network_id))
      .map((network) => network.network_id));

    const availableOptions: CrmPrimaryNetworkOption[] = this.liveAddLcNetworks
      .filter((option) => !linkedNetworkIds.has(option.network_id))
      .map((option) => ({ ...option }));
    const optionsById = new Map<number, CrmPrimaryNetworkOption>();
    availableOptions.forEach((option) => {
      optionsById.set(option.network_id, option);
    });

    this.getEditingClientNetworks()
      .filter((network) => this.hasAttachedLc(network))
      .forEach((network) => {
        const currentOption = this.buildPrimaryNetworkOptionFromClientNetwork(network);
        optionsById.set(currentOption.network_id, currentOption);
      });

    this.addLcOptions = Array.from(optionsById.values())
      .sort((left, right) => (left.network_name || '').localeCompare(right.network_name || ''));
  }

  private handleAddLcOptionsError(message: string): void {
    this.hasLoadedLiveAddLcOptions = false;
    this.liveAddLcNetworks = [];
    this.addLcOptions = [];
    this.addLcOptionsError = message;
    this.addLcOptionsPage = 1;
    this.isLoadingAddLcOptions = false;
  }

  private getSelectedAddLcOption(networkId: number): CrmPrimaryNetworkOption | null {
    const selectedOption = this.addLcOptions.find((option) => option.network_id === networkId) || null;

    if (selectedOption) {
      return selectedOption;
    }

    if (
      this.editingClientNetworkRow
      && this.getEditingClientNetworks().some((network) => this.hasAttachedLc(network) && network.network_id === networkId)
    ) {
      const currentNetwork = this.getEditingClientNetworks().find((network) => this.hasAttachedLc(network) && network.network_id === networkId);
      return currentNetwork ? this.buildPrimaryNetworkOptionFromClientNetwork(currentNetwork) : null;
    }

    return null;
  }

  private buildPrimaryNetworkOptionFromClientNetwork(network: CrmClientNetwork): CrmPrimaryNetworkOption {
    return {
      network_id: network.network_id,
      network_name: network.network_name || `LC #${network.network_id}`,
      client_name: this.getNetworkClientName(network),
      vendorId: network.vendorId,
      vendorName: network.vendorName || null,
      vendorUnknown: network.vendorUnknown,
      vendor: network.vendor === true,
      crm_status: network.crm_status || (network.vendor ? 'vendor' : 'workplace'),
      test_mode: network.test_mode,
      test_mode_note: network.test_mode_note,
      notes: network.notes
    };
  }

  private formatContactFieldLabel(value: string): string {
    return value
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .replace(/\bid\b/gi, 'ID')
      .replace(/\blc\b/gi, 'LC')
      .replace(/\b\w/g, (character) => character.toUpperCase());
  }

  private formatContactFieldValue(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(', ') : '-';
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return String(value);
  }

  private loadClientDetails(clientId: string, byClientId: boolean = false): void {
    if (!this.crmUser) {
      this.isLoadingDetails = false;
      return;
    }

    this.isClientDetailsRoute = byClientId;
    this.clientId = clientId;
    this.companyId = byClientId ? clientId : '';
    this.networkId = byClientId ? '' : clientId;
    this.isLoadingDetails = true;
    this.isEditingNetworkInfo = false;
    this.networkInfoError = '';
    this.setDetailsSupplementalLoading(false);
    this.detailsEnrichmentRequestId++;
    this.detailsError = '';
    this.dropdownDeleteError = '';
    this.activeDetailDropdown = null;
    this.vendorLc = null;
    this.tokenBreakdown = EMPTY_LC_TOKEN_BREAKDOWN;
    this.isVendorClient = false;
    this.managedVendorLcs = [];
    this.managedVendorLcRows = [];
    this.managedVendorLcTableData$.next([]);

    if (!clientId) {
      this.detailsError = 'No CRM LC was selected.';
      this.isLoadingDetails = false;
      return;
    }

    const detailsRequest = byClientId
      ? this.crmService.getClientDetailsByClientData(clientId)
      : this.crmService.getClientDetailsData(clientId);

    detailsRequest.subscribe(
      (details) => {
        this.clientId = details.companyId || details.clientId;
        this.companyId = details.companyId || (byClientId ? details.clientId : '');
        this.networkId = details.networkId || '';
        this.clientName = details.clientName;
        this.crmDropdowns = details.dropdowns || { ...EMPTY_CRM_DROPDOWNS };
        this.isVendorClient = !!details.isVendorClient;
        this.vendorLc = details.vendorLc || null;
        this.managedVendorLcs = details.managedVendorLcs || [];
        this.refreshManagedVendorLcTableRows();
        this.tokenBreakdown = details.tokenBreakdown || EMPTY_LC_TOKEN_BREAKDOWN;
        this.summary = details.summary;
        this.primaryContact = details.primaryContact;
        this.companyProfile = details.companyProfile;
        this.contactPersons = details.contactPersons;
        this.dates = details.dates;
        this.references = details.references;
        this.actions = details.actions || [];
        this.communications = details.communications;
        this.supportTickets = details.support;
        this.quickbooksCustomer = details.quickbooks.customer;
        this.quickbooksInvoices = details.quickbooks.invoices;
        this.marketing = details.marketing;
        this.admin = details.admin;
        this.billing = details.billing;
        this.referral = details.referral;
        this.notes = details.notes;
        this.activities = details.activities;
        this.networks = details.networks;
        this.network = details.network || null;
        this.networkInfo = details.networkInfo || null;
        this.metrics = details.metrics || null;
        this.primaryLcContact = details.primaryLcContact || null;
        this.lcContacts = details.lcContacts || [];
        this.subscription = details.subscription || null;
        this.users = details.users || [];
        this.changelog = details.changelog || [];
        this.editDraft = this.buildEditDraft(details.clientName, details.summary, details.primaryContact, details.billing, details.referral, details.network || null, details.subscription || null);
        this.initialEditDraftSnapshot = JSON.stringify(this.editDraft);
        this.hasPendingChanges = false;
        this.expandedSections = { ...DEFAULT_EXPANDED_SECTIONS };
        if (this.keepNotesActivityExpandedOnNextLoad) {
          this.expandedSections.notesActivity = true;
          this.keepNotesActivityExpandedOnNextLoad = false;
        }
        if (this.keepActionsExpandedOnNextLoad) {
          this.expandedSections.actions = true;
          this.keepActionsExpandedOnNextLoad = false;
        }
        this.resetEditingSections();
        this.currentActivityPage = 1;
        this.currentNetworkPage = 1;
        this.networkSearchTerm = '';
        this.selectedNetworkStage = 'all';
        this.selectedNetworkType = 'all';
        this.showAddLcsModal = false;
        this.showRemoveLcLinkConfirmModal = false;
        this.editingClientNetworkRow = null;
        this.addLcsError = '';
        this.removeLcLinkError = '';
        this.isRemovingLcLink = false;
        this.addLcOptionsError = '';
        this.showAddContactModal = false;
        this.addContactError = '';
        this.isSavingAdditionalContact = false;
        this.additionalContactDraft = this.getDefaultAdditionalContactDraft();
        this.showAddNoteModal = false;
        this.showEditNoteModal = false;
        this.showAddActivityModal = false;
        this.showAddActionModal = false;
        this.showCompleteActionConfirmModal = false;
        this.addNoteError = '';
        this.editNoteError = '';
        this.addActivityError = '';
        this.addActionError = '';
        this.completeActionError = '';
        this.isSavingManualNote = false;
        this.isSavingEditNote = false;
        this.isSavingManualActivity = false;
        this.isSavingClientAction = false;
        this.manualNoteDraft = this.getDefaultManualNoteDraft();
        this.editNoteDraft = null;
        this.manualActivityDraft = this.getDefaultManualActivityDraft();
        this.manualActionDraft = this.getDefaultManualActionDraft();
        this.actionPendingCompletion = null;
        this.completingActionIds.clear();
        this.disregardingActionIds.clear();
        this.addClientName = '';
        this.addClientStage = 'prospect';
        this.addClientType = 'lead';
        this.addLcSearchTerm = '';
        this.selectedAddLcNetworkIds = [];
        this.addLcOptionsPage = 1;
        this.selectedLcContactNetwork = null;
        this.selectedLcContactEntries = [];
        this.activeDetailDropdown = null;
        this.activeDetailTagDropdown = null;
        this.duplicateSearchTerm = '';
        this.duplicateSearchResults = details.companyProfile.duplicateCandidates;

        this.refreshTables();
        this.refreshAddLcOptions();
        this.isLoadingDetails = false;
        this.loadClientDetailsSupplementalData(clientId, byClientId);
        this.loadReferralCompanyOptions();
      },
      (error) => {
        this.detailsError = this.requestErrorMessage(error, 'Error loading CRM details.');
        this.isLoadingDetails = false;
        this.setDetailsSupplementalLoading(false);
      }
    );
  }

  private loadReferralCompanyOptions(): void {
    if (!this.companyId) {
      this.referralCompanyOptions = [];
      return;
    }

    this.crmService.getClientReferralOptions(this.companyId).subscribe(
      (options) => {
        this.referralCompanyOptions = options || [];
      },
      () => {
        this.referralCompanyOptions = [];
      }
    );

    this.loadReferralContactOptions();
  }

  private loadReferralContactOptions(): void {
    const sourceCompanyId = this.editDraft ? this.editDraft.referralSourceCompanyId : '';
    if (!sourceCompanyId) {
      this.referralContactOptions = [];
      return;
    }

    this.crmService.getClientContactOptions(sourceCompanyId).subscribe(
      (options) => {
        this.referralContactOptions = options || [];
      },
      () => {
        this.referralContactOptions = [];
      }
    );
  }

  private loadClientDetailsSupplementalData(clientId: string, byClientId: boolean): void {
    const requestId = ++this.detailsEnrichmentRequestId;
    const detailsId = byClientId ? (this.companyId || clientId) : (this.networkId || clientId);
    const shouldLoadLcData = this.hasLcDetails;
    const requests: Array<{
      type: CrmDetailsSupplementalRequest;
      request: Observable<Partial<CrmClientDetailsData>>;
    }> = [];

    this.setDetailsSupplementalLoading(false);

    if (shouldLoadLcData) {
      requests.push({
        type: 'lcData',
        request: byClientId
          ? this.crmService.getClientDetailsByClientLcData(detailsId)
          : this.crmService.getClientDetailsLcData(detailsId)
      });
      requests.push({
        type: 'metrics',
        request: byClientId
          ? this.crmService.getClientDetailsByClientMetricsData(detailsId)
          : this.crmService.getClientDetailsMetricsData(detailsId)
      });
      requests.push({
        type: 'users',
        request: byClientId
          ? this.crmService.getClientDetailsByClientUsersData(detailsId)
          : this.crmService.getClientDetailsUsersData(detailsId)
      });
    }

    if (this.isVendorClient) {
      requests.push({
        type: 'vendorLcs',
        request: byClientId
          ? this.crmService.getClientDetailsByClientVendorLcsData(detailsId)
          : this.crmService.getClientDetailsVendorLcsData(detailsId)
      });
    }

    if (requests.length === 0) {
      return;
    }

    requests.forEach((item) => {
      this.setDetailsSupplementalRequestLoading(item.type, true);
      item.request.subscribe(
        (details) => {
          if (requestId !== this.detailsEnrichmentRequestId) {
            return;
          }

          this.applyClientDetailsEnrichment(details);
          this.setDetailsSupplementalRequestLoading(item.type, false);
        },
        () => {
          if (requestId === this.detailsEnrichmentRequestId) {
            this.setDetailsSupplementalRequestLoading(item.type, false);
          }
        }
      );
    });
  }

  private applyClientDetailsEnrichment(details: Partial<CrmClientDetailsData>): void {
    let shouldRefreshEditDraft = false;

    if (details.companyId !== undefined && details.companyId !== null) {
      this.companyId = details.companyId;
    }
    if (details.networkId !== undefined) {
      this.networkId = details.networkId || '';
    }
    if (details.tokenBreakdown) {
      this.tokenBreakdown = details.tokenBreakdown;
    }
    if (details.networks) {
      this.networks = this.mergeClientNetworks(this.networks, details.networks);
    }
    if ('network' in details) {
      this.network = details.network || null;
      shouldRefreshEditDraft = true;
    }
    if ('networkInfo' in details) {
      this.networkInfo = details.networkInfo || null;
    }
    if ('metrics' in details) {
      this.metrics = details.metrics || null;
    }
    if ('primaryLcContact' in details) {
      this.primaryLcContact = details.primaryLcContact || null;
    }
    if (details.lcContacts) {
      this.lcContacts = details.lcContacts;
    }
    if ('subscription' in details) {
      this.subscription = details.subscription || null;
      shouldRefreshEditDraft = true;
    }
    if (details.users) {
      this.users = details.users;
    }
    if (details.changelog) {
      this.changelog = details.changelog;
    }
    if ('isVendorClient' in details) {
      this.isVendorClient = !!details.isVendorClient;
    }
    if ('vendorLc' in details) {
      this.vendorLc = details.vendorLc || null;
    }
    if (details.managedVendorLcs) {
      this.managedVendorLcs = details.managedVendorLcs;
      this.refreshManagedVendorLcTableRows();
    }

    if (shouldRefreshEditDraft) {
      this.refreshEditDraftFromCurrentDetailsIfSafe();
    }

    this.refreshTables();
  }

  private setDetailsSupplementalLoading(loading: boolean): void {
    this.isLoadingDetailsLcData = loading;
    this.isLoadingDetailsMetrics = loading;
    this.isLoadingDetailsUsers = loading;
    this.isLoadingDetailsManagedVendorLcs = loading;
    this.syncDetailsSupplementalLoading();
  }

  private setDetailsSupplementalRequestLoading(type: CrmDetailsSupplementalRequest, loading: boolean): void {
    switch (type) {
      case 'lcData':
        this.isLoadingDetailsLcData = loading;
        break;
      case 'metrics':
        this.isLoadingDetailsMetrics = loading;
        break;
      case 'users':
        this.isLoadingDetailsUsers = loading;
        break;
      case 'vendorLcs':
        this.isLoadingDetailsManagedVendorLcs = loading;
        break;
    }

    this.syncDetailsSupplementalLoading();
  }

  private syncDetailsSupplementalLoading(): void {
    this.isLoadingDetailsEnrichment = this.isLoadingDetailsLcData
      || this.isLoadingDetailsMetrics
      || this.isLoadingDetailsUsers
      || this.isLoadingDetailsManagedVendorLcs;
  }

  private refreshEditDraftFromCurrentDetailsIfSafe(): void {
    if (!this.editDraft || this.hasPendingChanges) {
      return;
    }

    this.editDraft = this.buildEditDraft(
      this.clientName,
      this.summary,
      this.primaryContact,
      this.billing,
      this.referral,
      this.network,
      this.subscription
    );
    this.initialEditDraftSnapshot = JSON.stringify(this.editDraft);
  }

  private mergeClientNetworks(currentNetworks: CrmClientNetwork[], incomingNetworks: CrmClientNetwork[]): CrmClientNetwork[] {
    const networkMap = new Map<string, CrmClientNetwork>();

    currentNetworks.forEach((network) => {
      networkMap.set(this.clientNetworkMergeKey(network), network);
    });

    incomingNetworks.forEach((network) => {
      const key = this.clientNetworkMergeKey(network);
      const existing = networkMap.get(key);
      networkMap.set(key, existing ? { ...existing, ...network } : network);
    });

    return Array.from(networkMap.values());
  }

  private clientNetworkMergeKey(network: CrmClientNetwork): string {
    const legacyNetwork = network as CrmClientNetwork & { networkID?: number };
    const networkId = Number(network.network_id || legacyNetwork.networkID || 0);
    if (networkId > 0) {
      return `network:${networkId}`;
    }

    return `link:${network.id || network.client_name || network.network_name || 'prospect'}`;
  }

  private requestErrorMessage(error: any, fallback: string): string {
    return (error && error.error && (error.error.message || error.error.error)) || (error && error.message) || fallback;
  }

  private hasStoredCrmActor(): boolean {
    const storedUser = this.crmService.getStoredCrmUser();
    if (!storedUser) {
      this.crmUser = null;
      return false;
    }

    this.crmUser = storedUser;
    return true;
  }

  private refreshTables(): void {
    const networkRows = this.networks.map((network) => this.mapNetworkRow(network));
    const changelogRows = this.changelog.map((entry) => this.mapChangeLogRow(entry));

    this.allNetworkTableRows = networkRows;
    this.refreshNetworkTablePage();
    this.refreshContactTableRows();
    this.refreshUserTableRows();
    this.refreshActionTableRows();
    this.changelogTableData$.next(changelogRows);

    if (changelogRows.length <= this.changelogItemsPerPage) {
      this.changelogTableRows = changelogRows;
    }
  }

  private refreshUserTableRows(): void {
    const userRows = this.users.map((user) => this.mapUserRow(user));
    this.userTableData$.next(userRows);

    if (userRows.length <= this.userItemsPerPage) {
      this.userTableRows = userRows;
    }
  }

  private refreshContactTableRows(): void {
    this.contactTableRows = this.buildContactTableRows();
  }

  private applyClientContactCreateResponse(contact: CrmContact | null, contacts: CrmContactPerson[]): void {
    if (contacts.length > 0) {
      this.contactPersons = contacts;
    } else if (contact) {
      const contactPerson = this.buildContactPersons([contact])[0];
      this.contactPersons = [
        ...this.contactPersons.filter((existingContact) => existingContact.id !== contactPerson.id),
        contactPerson
      ];
    }

    this.refreshContactTableRows();
  }

  private refreshActionTableRows(): void {
    const rows = [...(this.actions || [])]
      .filter((action) => this.showCompletedActions || !this.isClientActionClosed(action))
      .sort((first, second) => (
        Number(this.isClientActionClosed(first)) - Number(this.isClientActionClosed(second))
        || this.dateSortValue(first.actionDate) - this.dateSortValue(second.actionDate)
        || this.dateSortValue(second.created) - this.dateSortValue(first.created)
      ));

    this.actionTableData$.next(rows);
    this.actionTableRows = rows;
  }

  private applyClientActionMutationResponse(action: CrmActionItem | null, actions: CrmActionItem[]): void {
    if (actions.length > 0) {
      this.actions = actions;
    } else if (action) {
      this.actions = [
        ...this.actions.filter((existingAction) => existingAction.id !== action.id),
        action
      ];
    }

    this.expandedSections.actions = true;
    this.refreshActionTableRows();
  }

  private refreshManagedVendorLcTableRows(): void {
    const rows = this.managedVendorLcs.map((lc) => this.mapManagedVendorLcRow(lc));
    this.managedVendorLcTableData$.next(rows);
    this.managedVendorLcRows = rows.length <= this.managedVendorLcItemsPerPage
      ? rows
      : rows.slice(0, this.managedVendorLcItemsPerPage);
  }

  private scheduleContactDependentTableRefresh(): void {
    setTimeout(() => {
      this.refreshContactTableRows();
      this.refreshUserTableRows();
    });
  }

  private refreshNetworkTablePage(): void {
    const filteredRows = this.filteredNetworkTableRows;
    this.currentNetworkPage = Math.min(Math.max(this.currentNetworkPage, 1), this.totalNetworkPages);

    const start = (this.currentNetworkPage - 1) * this.networkItemsPerPage;
    this.networkTableRows = filteredRows.slice(start, start + this.networkItemsPerPage);
    this.networkTableData$.next(this.networkTableRows);
  }

  private mapNetworkRow(network: CrmClientNetwork): CrmClientNetworkRow {
    const primaryContact = network.primary_contact || null;
    const clientStage = this.getNetworkClientStage(network);
    const clientType = this.getNetworkClientType(network);
    const hasAttachedLc = this.hasAttachedLc(network);

    return {
      network,
      primaryContact,
      hasAttachedLc,
      clientName: this.getNetworkClientName(network),
      lcCount: this.getOwnedLcCount(network),
      clientStage,
      clientType,
      lcName: hasAttachedLc ? network.network_name : 'No LC assigned',
      contactName: hasAttachedLc ? this.getLcPrimaryContactName(primaryContact) : '-',
      contactEmail: hasAttachedLc && primaryContact && primaryContact.email ? primaryContact.email : '-',
      crmStatus: hasAttachedLc ? network.crm_status || '' : 'no-lc',
      primaryLabel: hasAttachedLc ? network.is_primary ? 'Primary' : 'Secondary' : 'No LC',
      tokenUsageLabel: hasAttachedLc ? this.getNetworkTokenUsageLabel(network) : '-',
      createdDate: network.created
    };
  }

  private mapManagedVendorLcRow(lc: CrmManagedVendorLc): CrmManagedVendorLcRow {
    const networkName = lc.networkName || `LC #${lc.networkId}`;

    return {
      lc,
      networkName,
      networkIdLabel: `#${lc.networkId}`,
      statusLabel: this.getDisplayLabel(lc.status || 'workplace'),
      createdDate: lc.created || ''
    };
  }

  private getNetworkSearchValues(row: CrmClientNetworkRow): string[] {
    return [
      row.clientName,
      row.lcName
    ].filter((value) => value !== null && value !== undefined && value !== '') as string[];
  }

  private getNetworkClientName(network: CrmClientNetwork): string {
    return network.client_name || this.displayClientName;
  }

  private getEditingClientNetworks(): CrmClientNetwork[] {
    return this.editingClientNetworkRow
      ? this.getClientNetworksForName(this.editingClientNetworkRow.clientName)
      : [];
  }

  private getClientNetworksForName(clientName: string): CrmClientNetwork[] {
    const normalizedClientName = this.normalizeClientName(clientName);

    return this.networks.filter((network) =>
      this.normalizeClientName(this.getNetworkClientName(network)) === normalizedClientName
    );
  }

  private getOwnedLcCount(network: CrmClientNetwork): number {
    const clientName = this.normalizeClientName(this.getNetworkClientName(network));

    return this.networks.filter((candidate) =>
      this.hasAttachedLc(candidate)
      && this.normalizeClientName(this.getNetworkClientName(candidate)) === clientName
    ).length;
  }

  private normalizeClientName(value: string): string {
    return value.trim().toLowerCase();
  }

  private getNetworkClientStage(network: CrmClientNetwork): CrmCompanyStage {
    return network.client_stage || 'client';
  }

  private getNetworkClientType(network: CrmClientNetwork): CrmCompanyType {
    const clientType = (network.client_type || (network.vendor ? 'internal' : 'customer')) as string;
    return (clientType === 'prospect' ? 'lead' : clientType) as CrmCompanyType;
  }

  private hasAttachedLc(network: CrmClientNetwork): boolean {
    return network.network_id > 0;
  }

  private normalizeClientNetworks(networks: CrmClientNetwork[]): CrmClientNetwork[] {
    const primaryNetwork = networks.find((network) => this.hasAttachedLc(network) && network.is_primary)
      || networks.find((network) => this.hasAttachedLc(network))
      || null;

    return this.sortClientNetworks(networks.map((network) => ({
      ...network,
      is_primary: !!primaryNetwork && this.hasAttachedLc(network) && network.id === primaryNetwork.id
    })));
  }

  private sortClientNetworks(networks: CrmClientNetwork[]): CrmClientNetwork[] {
    return networks.sort((left, right) =>
      Number(right.is_primary) - Number(left.is_primary)
      || Number(this.hasAttachedLc(right)) - Number(this.hasAttachedLc(left))
    );
  }

  private saveClientNetworkLinks(
    networks: CrmClientNetwork[],
    onSuccess?: () => void,
    clientUpdates?: CrmClientLinkFormUpdates
  ): void {
    const companyId = Number(this.clientId) || 0;
    if (companyId <= 0) {
      this.addLcsError = 'This record cannot be saved because it is missing a CRM company id.';
      return;
    }

    const normalizedNetworks = this.normalizeClientNetworks(networks);
    const primaryNetwork = normalizedNetworks.find((network) => this.hasAttachedLc(network) && network.is_primary) || null;
    const payload: Record<string, unknown> = {
      linked_networks: normalizedNetworks,
      primary_network: primaryNetwork
    };
    if (clientUpdates) {
      payload.name = clientUpdates.name.trim();
      payload.stage = clientUpdates.stage;
      payload.type = clientUpdates.type;
    }

    this.crmService.updateClient(companyId, payload).subscribe(
      () => {
        this.networks = normalizedNetworks;
        if (clientUpdates) {
          this.applyClientLinkFormUpdates(clientUpdates);
        }
        this.refreshTables();
        if (onSuccess) {
          onSuccess();
        }
      },
      (error) => {
        this.addLcsError = this.requestErrorMessage(error, 'Unable to save LC links.');
      }
    );
  }

  private persistClientNetworkLinks(networks: CrmClientNetwork[], clientUpdates?: CrmClientLinkFormUpdates): void {
    this.saveClientNetworkLinks(networks, undefined, clientUpdates);
  }

  private buildClientLinkFormUpdates(clientName: string): CrmClientLinkFormUpdates {
    return {
      name: clientName,
      stage: this.addClientStage,
      type: this.addClientType
    };
  }

  private applyClientLinkFormUpdates(updates: CrmClientLinkFormUpdates): void {
    const clientName = updates.name.trim();
    this.clientName = clientName;
    if (this.summary) {
      this.summary = {
        ...this.summary,
        stage: updates.stage,
        type: updates.type
      };
    }
    if (this.companyProfile) {
      this.companyProfile = {
        ...this.companyProfile,
        generalName: clientName
      };
    }
    if (this.editDraft) {
      this.editDraft = {
        ...this.editDraft,
        clientName,
        stage: updates.stage,
        type: updates.type
      };
      const savedSnapshot = this.parseEditDraftSnapshot();
      this.initialEditDraftSnapshot = JSON.stringify({
        ...savedSnapshot,
        clientName,
        stage: updates.stage,
        type: updates.type
      });
      this.hasPendingChanges = JSON.stringify(this.editDraft) !== this.initialEditDraftSnapshot;
    }
  }

  private parseEditDraftSnapshot(): Partial<CrmClientEditDraft> {
    try {
      return JSON.parse(this.initialEditDraftSnapshot || '{}') as Partial<CrmClientEditDraft>;
    } catch (error) {
      return {};
    }
  }

  private updateNetworkClientCrmFields(
    row: CrmClientNetworkRow,
    updates: Pick<CrmClientNetwork, 'client_stage' | 'client_type'>
  ): void {
    this.networks = this.networks.map((network) => (
      network.id === row.network.id
        ? { ...network, ...updates }
        : network
    ));
    this.saveClientNetworkLinks(this.networks);
    this.refreshTables();
  }

  private getLcPrimaryContactName(contact: CrmLcPrimaryContact | null): string {
    return contact && contact.name ? contact.name : 'No primary contact';
  }

  private getDefaultAdditionalContactDraft(): CrmAdditionalContactDraft {
    return {
      firstName: '',
      lastName: '',
      department: '',
      title: '',
      position: '',
      email: '',
      phone: '',
      officePhone: '',
      officeExtension: '',
      cellPhone: '',
      preferredContact: 'email',
      rolesCsv: '',
      tagsCsv: '',
      note: ''
    };
  }

  private getDefaultManualNoteDraft(): CrmManualNoteDraft {
    return {
      entityType: this.getDefaultManualLogEntityType(),
      pinned: true,
      body: ''
    };
  }

  private getDefaultManualActivityDraft(): CrmManualActivityDraft {
    return {
      entityType: 'user',
      created: this.getDateTimeInputValue(),
      action: '',
      details: ''
    };
  }

  private getDefaultManualActionDraft(): CrmManualActionDraft {
    return {
      level: 'Medium',
      action: 'follow_up_phone',
      details: '',
      actionDate: this.getDateTimeInputValue(),
      dueDate: this.getDateTimeInputValue()
    };
  }

  private getManualLogCompanyId(): number {
    const companyId = Number(this.companyId || (this.isClientDetailsRoute ? this.clientId : ''));
    return Number.isFinite(companyId) && companyId > 0 ? companyId : 0;
  }

  private getDefaultManualLogEntityType(): CrmEntityType {
    return !this.isClientDetailsRoute && this.getCurrentLcLinkId() > 0 ? 'lc' : 'company';
  }

  private getManualLogEntityId(_entityType: CrmEntityType | CrmActivityEntityType): number {
    return this.getManualLogCompanyId();
  }

  private dateSortValue(value: string | null | undefined): number {
    if (!value) {
      return 0;
    }

    const parsedDate = new Date(value);
    const timestamp = parsedDate.getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }

  private getCurrentLcLinkId(): number {
    const networks = this.networks || [];
    const currentNetworkId = Number(this.networkId);
    if (Number.isFinite(currentNetworkId) && currentNetworkId > 0) {
      const currentNetwork = networks.find((network) => Number(network.network_id) === currentNetworkId);
      if (currentNetwork && Number(currentNetwork.id) > 0) {
        return Number(currentNetwork.id);
      }
    }

    const primaryNetwork = networks.find((network) => network.is_primary && Number(network.id) > 0);
    if (primaryNetwork) {
      return Number(primaryNetwork.id);
    }

    const attachedNetwork = networks.find((network) => Number(network.id) > 0);
    return attachedNetwork ? Number(attachedNetwork.id) : 0;
  }

  private reloadCurrentDetails(expandNotesActivity: boolean = false, expandActions: boolean = false): void {
    this.keepNotesActivityExpandedOnNextLoad = expandNotesActivity;
    this.keepActionsExpandedOnNextLoad = expandActions;
    this.loadClientDetails(this.isClientDetailsRoute ? this.clientId : this.networkId, this.isClientDetailsRoute);
  }

  private getDateTimeInputValue(date: Date = new Date()): string {
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return localDate.toISOString().slice(0, 16);
  }

  private buildAdditionalContactPayload(): Record<string, unknown> {
    const draft = this.additionalContactDraft;
    const firstName = draft.firstName.trim();
    const lastName = draft.lastName.trim();
    const phone = this.nullIfEmpty(draft.phone);
    const officePhone = this.nullIfEmpty(draft.officePhone) || phone;

    return {
      first_name: firstName,
      last_name: lastName,
      department: this.nullIfEmpty(draft.department),
      title: this.nullIfEmpty(draft.title),
      position: this.nullIfEmpty(draft.position) || this.nullIfEmpty(draft.title),
      email: this.nullIfEmpty(draft.email),
      phone,
      phone_office: officePhone,
      phone_extension: this.nullIfEmpty(draft.officeExtension),
      phone_cell: this.nullIfEmpty(draft.cellPhone),
      phone_norm: this.normalizePhone(phone),
      preferred_contact: this.normalizePreferredContact(draft.preferredContact),
      contact_note: this.nullIfEmpty(draft.note),
      duplicate_key: this.buildContactDuplicateKey(firstName, lastName),
      role: this.splitCsv(draft.rolesCsv),
      is_primary_for_company: false,
      tags_csv: this.nullIfEmpty(draft.tagsCsv)
    };
  }

  private buildAdditionalContactDraftFromContact(contact: CrmContactPerson): CrmAdditionalContactDraft {
    return {
      firstName: contact.first_name || '',
      lastName: contact.last_name || '',
      department: contact.department || '',
      title: contact.title || '',
      position: contact.position || '',
      email: contact.email || '',
      phone: contact.phone || '',
      officePhone: contact.phone_office || contact.phone || '',
      officeExtension: contact.phone_extension || '',
      cellPhone: contact.phone_cell || '',
      preferredContact: this.normalizePreferredContact(contact.preferred_contact),
      rolesCsv: (contact.role || []).join(', '),
      tagsCsv: contact.tags_csv || '',
      note: contact.contact_note || ''
    };
  }

  private buildAdditionalContactPersonFromDraft(contactId: number, source: CrmContactPerson | null): CrmContactPerson {
    const draft = this.additionalContactDraft;
    const phone = this.nullIfEmpty(draft.phone);
    const officePhone = this.nullIfEmpty(draft.officePhone) || phone;
    const updated: CrmContactPerson = {
      ...(source || this.emptyContactPerson()),
      id: contactId,
      first_name: draft.firstName.trim(),
      last_name: draft.lastName.trim(),
      department: this.nullIfEmpty(draft.department),
      title: this.nullIfEmpty(draft.title),
      position: this.nullIfEmpty(draft.position) || this.nullIfEmpty(draft.title),
      email: this.nullIfEmpty(draft.email),
      phone,
      phone_office: officePhone,
      phone_extension: this.nullIfEmpty(draft.officeExtension),
      phone_cell: this.nullIfEmpty(draft.cellPhone),
      phone_norm: this.normalizePhone(phone),
      preferred_contact: this.normalizePreferredContact(draft.preferredContact),
      contact_note: this.nullIfEmpty(draft.note),
      role: this.splitCsv(draft.rolesCsv),
      is_primary_for_company: false,
      tags_csv: this.nullIfEmpty(draft.tagsCsv),
      company_id: source ? source.company_id : this.currentCompanyId(),
      modified: this.formatTimestamp(new Date()),
      displayName: '',
      duplicateCandidates: source ? source.duplicateCandidates : []
    };

    updated.displayName = this.getContactDisplayName(updated);
    return updated;
  }

  private buildContactPersons(contacts: CrmContact[]): CrmContactPerson[] {
    return contacts.map((contact) => ({
      ...contact,
      displayName: this.getContactDisplayName(contact),
      duplicateCandidates: []
    }));
  }

  private buildPrimaryContactTableRow(): CrmContactTableRow | null {
    const source = this.primaryContact;
    const draft = this.editDraft;
    const row = this.buildContactTableRow({
      ...(source || this.emptyContactPerson()),
      id: this.parseOptionalNumber(draft ? draft.primaryContactId : null) || (source ? source.id : -1),
      vendor_id: this.parseOptionalNumber(draft ? draft.primaryContactVendorId : null) || (source ? source.vendor_id : 0),
      first_name: draft ? draft.primaryContactFirstName : (source ? source.first_name : ''),
      last_name: draft ? draft.primaryContactLastName : (source ? source.last_name : ''),
      title: draft ? draft.primaryContactTitle : (source ? source.title : null),
      position: draft ? draft.primaryContactTitle : (source ? source.position || source.title : null),
      email: draft ? draft.primaryContactEmail : (source ? source.email : null),
      email_alt: draft ? draft.primaryContactAltEmail : (source ? source.email_alt : null),
      phone: draft ? draft.primaryContactPhone : (source ? source.phone : null),
      phone_office: draft ? draft.primaryContactPhone : (source ? source.phone_office || source.phone : null),
      phone_alt: draft ? draft.primaryContactAltPhone : (source ? source.phone_alt : null),
      phone_norm: draft ? this.normalizePhone(draft.primaryContactPhone) : (source ? source.phone_norm : null),
      preferred_contact: draft ? draft.primaryContactPreferredContact : (source ? source.preferred_contact : 'email'),
      company_id: this.parseOptionalNumber(draft ? draft.primaryContactCompanyId : null) || (source ? source.company_id : this.currentCompanyId()),
      user_id: this.parseOptionalNumber(draft ? draft.primaryContactUserId : null) || (source ? source.user_id : null),
      role: draft ? this.splitCsv(draft.primaryContactRolesCsv) : (source ? source.role : []),
      is_primary_for_company: true,
      tags_csv: draft ? draft.primaryContactTagsCsv : (source ? source.tags_csv : null),
      created: draft ? draft.primaryContactCreated : (source ? source.created : ''),
      modified: draft ? draft.primaryContactModified : (source ? source.modified : ''),
      deleted: draft ? draft.primaryContactDeleted || null : (source ? source.deleted : null),
      displayName: '',
      duplicateCandidates: []
    }, `primary:${source ? source.id : 'draft'}`, 'Primary Contact', false);

    row.displayName = this.getContactDisplayName(row);

    return this.hasContactTableRowData(row) ? row : null;
  }

  private buildBillingContactTableRow(): CrmContactTableRow | null {
    const draft = this.editDraft;
    const source = this.contactPersons.find((contact) => this.isBillingContact(contact)) || null;
    const billingName = draft ? draft.billingContactName : (this.billing ? this.billing.billingContactName || '' : '');
    const billingNameParts = this.splitContactName(billingName);
    const row = this.buildContactTableRow({
      ...this.emptyContactPerson(),
      id: source ? source.id : -2,
      vendor_id: source ? source.vendor_id : 0,
      first_name: billingNameParts.firstName,
      last_name: billingNameParts.lastName,
      title: 'Billing',
      position: 'Billing',
      email: draft ? draft.billingEmail : (this.billing ? this.billing.billingEmail : null),
      phone: draft ? draft.billingPhone : (this.billing ? this.billing.billingPhone : null),
      phone_office: draft ? draft.billingPhone : (this.billing ? this.billing.billingPhone : null),
      phone_norm: draft ? this.normalizePhone(draft.billingPhone) : this.normalizePhone(this.billing ? this.billing.billingPhone : null),
      preferred_contact: source ? source.preferred_contact : null,
      company_id: source ? source.company_id : this.currentCompanyId(),
      role: ['billing'],
      is_primary_for_company: false,
      contact_note: draft ? draft.billingInstructions : (this.billing ? this.billing.billingInstructions : null),
      displayName: '',
      duplicateCandidates: []
    }, `billing:${source ? source.id : 'draft'}`, 'Billing', false);

    row.displayName = this.getContactDisplayName(row);

    return this.hasContactTableRowData(row) ? row : null;
  }

  private buildContactTableRow(
    contact: CrmContactPerson,
    tableRowId: string,
    contactRoleLabel: string,
    canDelete: boolean
  ): CrmContactTableRow {
    return {
      ...contact,
      tableRowId,
      contactRoleLabel,
      canDelete
    };
  }

  private emptyContactPerson(): CrmContactPerson {
    return {
      id: 0,
      vendor_id: 0,
      first_name: '',
      last_name: '',
      department: null,
      title: null,
      position: null,
      email: null,
      email_alt: null,
      phone: null,
      phone_office: null,
      phone_extension: null,
      phone_cell: null,
      phone_alt: null,
      phone_norm: null,
      preferred_contact: null,
      contact_note: null,
      company_id: this.currentCompanyId(),
      user_id: null,
      role: [],
      is_primary_for_company: false,
      tags_csv: null,
      created: '',
      modified: '',
      deleted: null,
      displayName: '',
      duplicateCandidates: []
    };
  }

  private currentCompanyId(): number | null {
    const companyId = Number(this.clientId) || 0;
    return companyId > 0 ? companyId : null;
  }

  private splitContactName(name: string): { firstName: string; lastName: string } {
    const parts = name.trim().split(/\s+/).filter((part) => part.length > 0);
    if (parts.length <= 1) {
      return { firstName: parts[0] || '', lastName: '' };
    }

    return {
      firstName: parts.slice(0, -1).join(' '),
      lastName: parts[parts.length - 1]
    };
  }

  private hasContactTableRowData(contact: CrmContact): boolean {
    return [
      contact.first_name,
      contact.last_name,
      contact.email,
      contact.email_alt,
      contact.phone,
      contact.phone_office,
      contact.phone_cell,
      contact.phone_alt,
      contact.contact_note
    ].some((value) => this.contactValueAsString(value).trim().length > 0);
  }

  private canDeleteContactTableRow(contact: CrmContactPerson): boolean {
    return contact['canDelete'] !== false;
  }

  private setContactDeleting(contactId: number, deleting: boolean): void {
    const nextDeletingIds = new Set(this.deletingContactIds);
    if (deleting) {
      nextDeletingIds.add(contactId);
    } else {
      nextDeletingIds.delete(contactId);
    }

    this.deletingContactIds = nextDeletingIds;
  }

  private getContactDisplayName(contact: CrmContact): string {
    const name = `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
    return name || contact.email || contact.phone || 'Unnamed contact';
  }

  private buildContactDuplicateKey(firstName: string, lastName: string): string | null {
    const key = `${firstName}-${lastName}`.trim().toLowerCase().replace(/\s+/g, '-');
    return key === '-' || key.length === 0 ? null : key;
  }

  private nullIfEmpty(value: string | null | undefined): string | null {
    const trimmed = (value || '').trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  private parseOptionalNumber(value: string | null | undefined): number | null {
    const trimmed = (value || '').trim();
    if (!trimmed) {
      return null;
    }

    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private normalizePhone(value: string | null): string | null {
    if (!value) {
      return null;
    }

    const normalized = value.replace(/\D+/g, '');
    return normalized.length > 0 ? normalized : null;
  }

  private normalizePreferredContact(value: string | null | undefined): 'email' | 'phone' {
    return (value || '').trim().toLowerCase() === 'phone' ? 'phone' : 'email';
  }

  private buildAltenateAccountContact(
    name: string | null | undefined,
    email: string | null | undefined,
    phone: string | null | undefined
  ): { name: string; email: string; phone: string; } | null {
    const normalizedName = (name || '').trim();
    const normalizedEmail = (email || '').trim();
    const normalizedPhone = (phone || '').trim();

    if (![normalizedName, normalizedEmail, normalizedPhone].some((value) => value.length > 0)) {
      return null;
    }

    return {
      name: normalizedName,
      email: normalizedEmail,
      phone: normalizedPhone
    };
  }

  private getNetworkTokenUsageLabel(network: CrmClientNetwork): string {
    if (!network.metrics) {
      return '-';
    }

    const usedTokens = network.metrics.tokens_used || 0;
    const unusedTokens = Math.max((network.metrics.tokens_total || 0) - usedTokens, 0);
    return `${this.formatTokenCount(usedTokens)}/${this.formatTokenCount(unusedTokens)}`;
  }

  private buildDetailsBreakdown(labels: string[], preferredOrder: string[] = []): CrmBreakdownItem[] {
    const counts = labels
      .filter((label) => !!label)
      .reduce((map, label) => {
        map.set(label, (map.get(label) || 0) + 1);
        return map;
      }, new Map<string, number>());

    const orderedKeys = preferredOrder.filter((label) => counts.has(label));
    const unorderedKeys = Array.from(counts.keys())
      .filter((label) => !preferredOrder.includes(label))
      .sort((left, right) => left.localeCompare(right));

    return [...orderedKeys, ...unorderedKeys].map((label) => ({
      key: label,
      label: this.getDisplayLabel(label),
      count: counts.get(label) || 0
    }));
  }

  private formatTokenCount(value: number): string {
    return value.toLocaleString('en-US');
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private createClientNetworkLink(
    option: CrmPrimaryNetworkOption,
    isPrimary: boolean,
    linkId: number,
    clientName: string,
    clientStage: CrmCompanyStage,
    clientType: CrmCompanyType
  ): CrmClientNetwork {
    const now = this.formatTimestamp(new Date());
    return {
      id: 0,
      company_id: Number(this.clientId) || 0,
      network_id: option.network_id,
      network_name: option.network_name,
      client_name: clientName,
      client_stage: clientStage,
      client_type: clientType,
      is_primary: isPrimary,
      vendorId: option.vendorId,
      vendorName: option.vendorName || null,
      vendorUnknown: option.vendorUnknown,
      vendor: option.vendor,
      test_mode: option.test_mode,
      test_mode_note: option.test_mode_note,
      crm_status: option.crm_status,
      notes: option.notes,
      created: now,
      modified: now,
      metrics: null,
      primary_contact: null
    };
  }

  private createClientProspectLink(
    linkId: number,
    clientName: string,
    clientStage: CrmCompanyStage,
    clientType: CrmCompanyType
  ): CrmClientNetwork {
    const now = this.formatTimestamp(new Date());
    return {
      id: 0,
      company_id: Number(this.clientId) || 0,
      network_id: 0,
      network_name: '',
      client_name: clientName,
      client_stage: clientStage,
      client_type: clientType,
      is_primary: false,
      vendor: false,
      test_mode: false,
      test_mode_note: null,
      crm_status: null,
      notes: 'Prospect without an assigned LC.',
      created: now,
      modified: now,
      metrics: null,
      primary_contact: null
    };
  }

  private formatTimestamp(date: Date): string {
    return date.toISOString().slice(0, 19);
  }

  private formatDateInputValue(value: string | null | undefined): string {
    if (!value) {
      return '';
    }

    const trimmedValue = value.trim();
    const dateMatch = trimmedValue.match(/^(\d{4}-\d{2}-\d{2})/);
    if (dateMatch) {
      return dateMatch[1];
    }

    const parsedDate = new Date(trimmedValue);
    return Number.isNaN(parsedDate.getTime()) ? '' : parsedDate.toISOString().slice(0, 10);
  }

  private mapUserRow(user: CrmClientUser): CrmClientUserRow {
    const highlightedRoles = this.getUserHighlightedRoles(user);

    return {
      user,
      userName: user.name,
      email: user.email,
      roleLabel: user.role.length > 0 ? user.role.join(', ') : (highlightedRoles.length > 0 ? '' : '-'),
      highlightedRoles,
      groupsLabel: this.getUserGroupsLabel(user),
      statusLabel: user.status,
      lastActivityDate: user.lastActivity
    };
  }

  private getUserHighlightedRoles(user: CrmClientUser): string[] {
    const email = this.normalizeContactMatchValue(user.email);
    if (!email || email === '-') {
      return [];
    }

    const roles: string[] = [];
    if (this.getPrimaryContactEmails().includes(email)) {
      roles.push('Primary');
    }
    if (this.getBillingContactEmails().includes(email)) {
      roles.push('Billing');
    }

    return roles;
  }

  getHighlightedUserRoleClass(role: string): string {
    return role === 'Billing' ? 'user-role-badge-billing' : 'user-role-badge-primary';
  }

  private getUserRolePrintLabel(user: CrmClientUser): string {
    const roles = [
      ...this.getUserHighlightedRoles(user),
      ...user.role
    ];

    return roles.length > 0 ? roles.join(', ') : '-';
  }

  private getPrimaryContactEmails(): string[] {
    const primaryContact = this.contactPersons.find((contact) => this.isPrimaryContact(contact)) || null;
    const values = [
      this.editDraft ? this.editDraft.primaryContactEmail : null,
      this.editDraft ? this.editDraft.primaryContactAltEmail : null,
      this.primaryContact ? this.primaryContact.email : null,
      this.primaryContact ? this.primaryContact.email_alt : null,
      primaryContact ? primaryContact.email : null,
      primaryContact ? primaryContact.email_alt : null
    ];

    return this.uniqueNormalizedEmails(values);
  }

  private getBillingContactEmails(): string[] {
    const billingContact = this.contactPersons.find((contact) => this.isBillingContact(contact)) || null;

    return this.uniqueNormalizedEmails([
      this.editDraft ? this.editDraft.billingEmail : null,
      this.billing ? this.billing.billingEmail : null,
      billingContact ? billingContact.email : null,
      billingContact ? billingContact.email_alt : null
    ]);
  }

  private uniqueNormalizedEmails(values: Array<string | null | undefined>): string[] {
    const emails: string[] = [];
    values.forEach((value) => {
      const email = this.normalizeContactMatchValue(value);
      if (email && !emails.includes(email)) {
        emails.push(email);
      }
    });

    return emails;
  }

  private getUserGroupsLabel(user: CrmClientUser): string {
    if (user.groups && user.groups.length > 0) {
      return user.groups.join(', ');
    }

    return user.team || '-';
  }

  private mapChangeLogRow(entry: CrmClientChangeLogEntry): CrmClientChangeLogRow {
    return {
      entry,
      changedDate: entry.changedAt,
      actor: entry.actor,
      scopeLabel: this.getDisplayLabel(entry.scope),
      target: entry.target,
      actionLabel: this.getDisplayLabel(entry.action),
      changesSummary: entry.changes
        .map((change) => `${change.field}: ${change.oldValue} -> ${change.newValue}`)
        .join(' | ')
    };
  }

  private buildEditDraft(
    clientName: string,
    summary: CrmClientSummary,
    primaryContact: CrmContact | null,
    billing: CrmClientBilling | null,
    referral: CrmClientReferral | null,
    network: CrmLcNetwork | null,
    subscription: CrmLcSubscription | null
  ): CrmClientEditDraft {
    const alternateAccountContact = billing ? billing.alternateAccountContact : null;

    return {
      clientName,
      legalName: summary.legalName || '',
      stage: summary.stage,
      type: summary.type || '',
      source: summary.source || '',
      converted: summary.converted || '',
      industry: summary.industry || '',
      website: summary.website || '',
      phone: summary.phone || '',
      emailGeneral: summary.emailGeneral || '',
      addressText: summary.addressLines.join('\n'),
      tagsCsv: summary.tags.join(', '),
      notesSummary: summary.notesSummary || '',
      primaryContactId: primaryContact ? String(primaryContact.id) : '',
      primaryContactVendorId: primaryContact ? String(primaryContact.vendor_id) : '',
      primaryContactFirstName: primaryContact ? primaryContact.first_name || '' : '',
      primaryContactLastName: primaryContact ? primaryContact.last_name || '' : '',
      primaryContactTitle: primaryContact ? primaryContact.title || '' : '',
      primaryContactEmail: primaryContact ? primaryContact.email || '' : '',
      primaryContactAltEmail: primaryContact ? primaryContact.email_alt || '' : '',
      primaryContactPhone: primaryContact ? primaryContact.phone || '' : '',
      primaryContactAltPhone: primaryContact ? primaryContact.phone_alt || '' : '',
      primaryContactPhoneNorm: primaryContact ? primaryContact.phone_norm || '' : '',
      primaryContactPreferredContact: this.normalizePreferredContact(primaryContact ? primaryContact.preferred_contact : null),
      primaryContactCompanyId: primaryContact && primaryContact.company_id ? String(primaryContact.company_id) : '',
      primaryContactUserId: primaryContact && primaryContact.user_id ? String(primaryContact.user_id) : '',
      primaryContactRolesCsv: primaryContact ? primaryContact.role.join(', ') : '',
      primaryContactIsPrimary: primaryContact ? primaryContact.is_primary_for_company : true,
      primaryContactTagsCsv: primaryContact ? primaryContact.tags_csv || '' : '',
      primaryContactNotes: primaryContact ? primaryContact.contact_note || '' : '',
      primaryContactCreated: primaryContact ? primaryContact.created || '' : '',
      primaryContactModified: primaryContact ? primaryContact.modified || '' : '',
      primaryContactDeleted: primaryContact ? primaryContact.deleted || '' : '',
      billingContactName: billing ? billing.billingContactName || '' : '',
      billingEmail: billing ? billing.billingEmail || '' : '',
      billingPhone: billing ? billing.billingPhone || '' : '',
      primaryRegistrarEmail: billing ? billing.primaryRegistrarEmail || '' : '',
      alternateAccountContactName: alternateAccountContact ? alternateAccountContact.name : '',
      alternateAccountContactEmail: alternateAccountContact ? alternateAccountContact.email : '',
      alternateAccountContactPhone: alternateAccountContact ? alternateAccountContact.phone : '',
      billingInstructions: billing ? billing.billingInstructions || '' : '',
      lcNetworkId: network ? String(network.networkID) : '',
      lcName: network ? network.network_name || '' : '',
      lcLoginPage: network ? network.loginPage || '' : '',
      lcSubscriptionStart: this.formatDateInputValue(subscription ? subscription.subscriptionStart : null),
      lcSubscriptionEnd: this.formatDateInputValue(subscription ? subscription.subscriptionEnd : null),
      lcAdvancePay: subscription ? subscription.advancePay : false,
      lcInvoiceRef: subscription ? subscription.invoiceRef || '' : '',
      lcNote: subscription ? subscription.note || '' : '',
      referralSourceCompanyId: referral && referral.sourceCompanyId !== null && referral.sourceCompanyId !== undefined ? String(referral.sourceCompanyId) : '',
      referralSourceCompanyName: referral ? referral.sourceCompanyName || '' : '',
      referralSourceContactId: referral && referral.sourceContactId !== null && referral.sourceContactId !== undefined ? String(referral.sourceContactId) : '',
      referralSourceContactName: referral ? referral.sourceContactName || '' : '',
      referralProgram: referral ? referral.program || '' : '',
      referred: this.formatDateInputValue(referral ? referral.referred : null),
      referralNotes: referral ? referral.notes || '' : ''
    };
  }

  private splitCsv(value: string): string[] {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  }

  private detailTagDropdownName(target: CrmDetailTagDropdownTarget): keyof CrmDropdownOptions {
    return target === 'companyProfileTags' || target === 'overviewTags'
      ? 'client_tags'
      : 'user_tags';
  }

  private dropdownDeleteKey(name: keyof CrmDropdownOptions, item: string): string {
    return `${name}:${String(item || '').trim().toLowerCase()}`;
  }

  private detailTagDropdownValue(target: CrmDetailTagDropdownTarget): string {
    if (target === 'additionalContactTags') {
      return this.additionalContactDraft.tagsCsv || '';
    }

    if (!this.editDraft) {
      return '';
    }

    return target === 'primaryContactTags'
      ? this.editDraft.primaryContactTagsCsv || ''
      : this.editDraft.tagsCsv || '';
  }

  private setDetailTagDropdownValue(target: CrmDetailTagDropdownTarget, value: string): void {
    if (target === 'additionalContactTags') {
      this.additionalContactDraft.tagsCsv = value;
      return;
    }

    if (!this.editDraft) {
      return;
    }

    if (target === 'primaryContactTags') {
      this.editDraft.primaryContactTagsCsv = value;
    } else {
      this.editDraft.tagsCsv = value;
    }
    this.onEditDraftChange();
  }

  private currentCsvTagSegment(value: string): string {
    const segments = String(value || '').split(',');
    return (segments.pop() || '').trim();
  }

  private replaceCurrentCsvTag(value: string, item: string): string {
    const segments = String(value || '').split(',');
    segments.pop();
    const tags = [
      ...segments.map((segment) => segment.trim()).filter(Boolean),
      item.trim()
    ];

    return Array.from(new Map(tags.map((tag) => [tag.toLowerCase(), tag])).values()).join(', ');
  }

  private resetEditingSections(): void {
    this.editingSections = { ...DEFAULT_EDITING_SECTIONS };
  }
}
